const STORAGE_KEY = "orderWorkflowControl.v1";
const CHAT_DRAFTS_KEY = "orderWorkflowControl.chatDrafts.v1";
const WORKFLOW_STATE_API = "/.netlify/functions/workflow-state";

const ROLES = [
  { id: "sales_bd", title: "Sales & Business Development" },
  { id: "sales_admin_export", title: "Sales Admin / Export" },
  { id: "account", title: "Account" },
  { id: "sales_coordinator", title: "Sales Coordinator" },
  { id: "qc_certification", title: "QC / Production / Certification" },
  { id: "purchase_import", title: "Purchase / Import" },
  { id: "procurement_support", title: "Procurement / Production Support" },
  { id: "factory_manager", title: "Factory Manager" },
  { id: "admin_hr", title: "Shipping" },
  { id: "operations_head", title: "COO / Operations Head" },
  { id: "ceo", title: "CEO" },
];

const FIELD_TYPES = {
  short: "text",
  date: "date",
  number: "number",
  long: "textarea",
};

const STAGES = [
  {
    id: "intake_validation",
    name: "Sales Intake & Validation",
    sla: "48 hours from inquiry receipt",
    slaHours: 48,
    advanceRoles: ["sales_bd", "sales_coordinator"],
    editors: ["sales_bd", "sales_admin_export", "qc_certification"],
    fields: [
      { id: "customerRef", label: "Customer reference", type: "short", required: true },
      { id: "destinationCountry", label: "Destination country", type: "short", required: true },
      { id: "inquirySummary", label: "Inquiry summary", type: "long", required: true },
      { id: "specBenchmark", label: "Internal specification benchmark", type: "long", required: true },
      { id: "regulatoryRequirement", label: "Export regulatory requirement", type: "long", required: true },
    ],
    tasks: [
      {
        id: "inquiry_captured",
        label: "Customer inquiry captured",
        detail: "Customer request, destination, and expected product scope are recorded.",
        roles: ["sales_bd"],
      },
      {
        id: "spec_benchmarked",
        label: "Specification benchmark completed",
        detail: "Inquiry is checked against internal product specifications before quoting.",
        roles: ["sales_admin_export"],
      },
      {
        id: "regulatory_checked",
        label: "Regulatory requirement verified",
        detail: "Export country rules and production standard requirements are confirmed.",
        roles: ["qc_certification", "sales_bd"],
      },
    ],
  },
  {
    id: "quote_pi",
    name: "Quotation & PI",
    sla: "24 hours after sales intake checkpoint",
    slaHours: 24,
    advanceRoles: ["sales_bd", "sales_coordinator"],
    editors: ["sales_admin_export", "sales_bd"],
    fields: [
      { id: "quotationRef", label: "Quotation reference", type: "short", required: true },
      { id: "quotationValue", label: "Quotation value", type: "number", required: true },
      { id: "piRef", label: "Pro-forma invoice reference", type: "short", required: true },
      { id: "piIssueDate", label: "PI issued date", type: "date", required: true },
      { id: "piValue", label: "Pro-forma invoice value", type: "number", required: true },
      { id: "estimatedShippingDate", label: "Estimated order delivery / shipping date", type: "date", required: true },
      { id: "quoteConfirmation", label: "Customer quote confirmation", type: "long", required: true },
    ],
    tasks: [
      {
        id: "quote_prepared",
        label: "Quotation and PI prepared",
        detail: "Quote and pro-forma invoice are ready for customer confirmation.",
        roles: ["sales_admin_export"],
      },
      {
        id: "quote_sent_confirmed",
        label: "Quote and PI sent to customer",
        detail: "Customer receives the commercial documents and confirms the order.",
        roles: ["sales_bd"],
      },
    ],
  },
  {
    id: "deposit_jo",
    name: "Deposit & JO Launch",
    sla: "Deposit logged within 24 hours; JO issued within 24 hours after confirmation",
    slaHours: 24,
    advanceRoles: ["sales_coordinator"],
    editors: ["account", "sales_coordinator", "sales_admin_export"],
    fields: [
      { id: "depositRef", label: "Deposit payment reference", type: "short", required: true },
      { id: "depositValue", label: "Deposit received value", type: "number", required: true },
      { id: "depositDate", label: "Deposit confirmation date", type: "date", required: true },
      { id: "jobOrderRef", label: "Job Order reference", type: "short", required: true },
      { id: "orderOwner", label: "Order owner role", type: "short", required: true },
    ],
    tasks: [
      {
        id: "deposit_confirmed",
        label: "Deposit confirmed",
        detail: "No Job Order can be issued before incoming funds are confirmed.",
        roles: ["account"],
      },
      {
        id: "jo_created",
        label: "Job Order created and transmitted",
        detail: "JO is assigned to the order owner and sent to planning stakeholders.",
        roles: ["sales_coordinator", "sales_admin_export"],
      },
    ],
  },
  {
    id: "technical_signoff",
    name: "Technical Sign-off",
    sla: "48 hours from JO creation",
    slaHours: 48,
    advanceRoles: ["qc_certification", "sales_coordinator"],
    editors: ["qc_certification", "sales_coordinator"],
    fields: [
      { id: "processRequirements", label: "Process requirements", type: "long", required: true },
      { id: "complianceNotes", label: "Compliance alignment notes", type: "long", required: true },
      { id: "blockedReason", label: "Blocked reason if not ready", type: "long", required: false },
    ],
    tasks: [
      {
        id: "jo_technical_ready",
        label: "JO technically ready",
        detail: "Product specs, process requirements, and compliance alignment are signed off.",
        roles: ["qc_certification"],
      },
    ],
  },
  {
    id: "procurement_readiness",
    name: "Procurement & Material Readiness",
    sla: "POs within 48 hours of JO approval; material reminder after 40 days",
    slaHours: 48,
    advanceRoles: ["sales_coordinator"],
    editors: [
      "purchase_import",
      "procurement_support",
      "qc_certification",
      "sales_bd",
      "sales_coordinator",
    ],
    fields: [
      { id: "linkedRawMaterialPoId", label: "Linked stock raw material PO", type: "po-select", required: false },
      { id: "rawMaterialPoDate", label: "Raw material PO date", type: "date", required: true },
      { id: "rawMaterialPo", label: "Raw material PO reference", type: "short", required: true },
      { id: "rawMaterialEta", label: "Raw material ETA", type: "date", required: true },
      { id: "materialStatusReminder", label: "40-day material status reminder", type: "date", required: true, generated: true },
      { id: "packagingRequirement", label: "Packaging and consumable requirement", type: "long", required: true },
      { id: "packagingPo", label: "Packaging / consumable PO reference", type: "short", required: true },
      { id: "packagingPoDate", label: "Packaging / consumable PO date", type: "date", required: true },
      { id: "packagingMaterialEta", label: "Packaging / consumable material ETA", type: "date", required: true },
      { id: "packagingStatusReminder", label: "14-day packaging / consumable reminder", type: "date", required: true, generated: true },
    ],
    tasks: [
      {
        id: "raw_material_po",
        label: "Raw material PO issued",
        detail: "Raw material order is placed and ETA is recorded.",
        roles: ["purchase_import"],
      },
      {
        id: "packaging_calculated",
        label: "Packaging and consumables calculated",
        detail: "All packaging, consumables, and quantities are calculated against the JO.",
        roles: ["procurement_support"],
      },
      {
        id: "packaging_technical_verified",
        label: "Packaging technical verification",
        detail: "Packaging and consumables are checked for technical accuracy.",
        roles: ["qc_certification"],
      },
      {
        id: "packaging_customer_confirmed",
        label: "Packaging confirmation before PO release",
        detail: "No packaging PO is released until customer-facing confirmation is complete.",
        roles: ["sales_bd"],
      },
      {
        id: "packaging_ready",
        label: "Packaging readiness confirmed",
        detail: "Order owner confirms material readiness or escalates if not ready.",
        roles: ["sales_coordinator"],
      },
    ],
  },
  {
    id: "production_qc",
    name: "Production Planning & QC",
    sla: "Production green light within 24 hours of start; final QC within 24 hours after finish",
    slaHours: 24,
    advanceRoles: ["sales_coordinator", "qc_certification"],
    editors: ["factory_manager", "sales_coordinator", "qc_certification"],
    fields: [
      { id: "productionStart", label: "Production start date", type: "date", required: true },
      { id: "productionFinish", label: "Production finish date", type: "date", required: true },
      { id: "readySchedule", label: "Ready schedule shared by production", type: "short", required: true },
      { id: "qcFindings", label: "QC findings", type: "long", required: true },
    ],
    tasks: [
      {
        id: "production_schedule_confirmed",
        label: "Production schedule confirmed",
        detail: "Production plan is set based on JO and material readiness.",
        roles: ["factory_manager"],
      },
      {
        id: "department_readiness_tracked",
        label: "Department readiness coordinated",
        detail: "Order owner tracks raw materials, packaging, and production readiness.",
        roles: ["sales_coordinator"],
      },
      {
        id: "production_green_light",
        label: "Production green light signed off",
        detail: "Conformity and regulatory compliance are checked after production starts.",
        roles: ["qc_certification"],
      },
      {
        id: "final_qc_signed",
        label: "Final quality and compliance sign-off",
        detail: "Finished goods meet agreed specifications and export requirements.",
        roles: ["qc_certification"],
      },
    ],
  },
  {
    id: "shipping_docs",
    name: "Shipping & Documentation",
    sla: "Schedule/rates within 72 hours; shipping docs within 48 hours after QC sign-off",
    slaHours: 72,
    advanceRoles: ["admin_hr"],
    editors: ["admin_hr", "sales_admin_export", "procurement_support", "sales_bd"],
    fields: [
      { id: "freightSchedule", label: "Shipping schedule and rate summary", type: "long", required: true },
      { id: "bookingRef", label: "Booking reference", type: "short", required: true },
      { id: "commercialInvoiceRef", label: "Commercial invoice reference", type: "short", required: true },
      { id: "certificateOriginRef", label: "Certificate of origin reference", type: "short", required: true },
      { id: "customDeclarationRef", label: "Custom declaration reference", type: "short", required: true },
      { id: "packingListRef", label: "Packing list reference", type: "short", required: true },
    ],
    tasks: [
      {
        id: "schedule_rates_shared",
        label: "Shipping schedule and rates furnished",
        detail: "Customer-facing schedule and rates are shared for confirmation.",
        roles: ["admin_hr"],
      },
      {
        id: "booking_confirmed",
        label: "Shipment booked",
        detail: "Booking is confirmed after rate and schedule approval.",
        roles: ["admin_hr"],
      },
      {
        id: "shipping_docs_ready",
        label: "Shipping documents prepared",
        detail: "COO, commercial invoice, and custom declaration are ready.",
        roles: ["sales_admin_export"],
      },
      {
        id: "packing_list_ready",
        label: "Packing list ready",
        detail: "Packing list is provided for shipment documentation.",
        roles: ["procurement_support"],
      },
    ],
  },
  {
    id: "closing_release",
    name: "Final Payment & Document Release",
    sla: "No final document release until payment is clear",
    slaHours: 24,
    advanceRoles: ["account", "sales_bd"],
    editors: ["account", "sales_bd"],
    fields: [
      { id: "finalInvoiceRef", label: "Final invoice reference", type: "short", required: true },
      { id: "finalPaymentRef", label: "Final payment reference", type: "short", required: true },
      { id: "finalPaymentValue", label: "Final payment received value", type: "number", required: true },
      { id: "releaseDate", label: "Document release date", type: "date", required: true },
    ],
    tasks: [
      {
        id: "invoice_logged",
        label: "Invoice logged in accounting system",
        detail: "Invoice is recorded and final payment is tracked.",
        roles: ["account"],
      },
      {
        id: "final_payment_confirmed",
        label: "Final payment confirmed",
        detail: "No payment means no document release.",
        roles: ["account"],
      },
      {
        id: "documents_released",
        label: "Final documents released to customer",
        detail: "Customer receives final documents only after payment clearance.",
        roles: ["sales_bd"],
      },
    ],
  },
];

const DEFAULT_ACCESS = {
  roles: Object.fromEntries(
    ROLES.map((role) => [
      role.id,
      {
        canCreateOrder: ["sales_bd", "sales_coordinator", "admin_hr", "ceo"].includes(role.id),
        canManageAccess: role.id === "ceo",
        canViewSpecial: role.id === "ceo" || role.id === "operations_head",
        canViewPurchaseOrders: [
          "purchase_import",
          "procurement_support",
          "qc_certification",
          "sales_bd",
          "sales_coordinator",
          "operations_head",
          "ceo",
        ].includes(role.id),
        canManagePurchaseOrders: role.id === "purchase_import" || role.id === "ceo",
        canBypassToProcurement: role.id === "operations_head" || role.id === "ceo",
        canAdvanceAny: false,
        canEditAny: false,
      },
    ]),
  ),
  stageEditors: Object.fromEntries(STAGES.map((stage) => [stage.id, [...stage.editors]])),
  stageAdvance: Object.fromEntries(STAGES.map((stage) => [stage.id, [...stage.advanceRoles]])),
  taskSigners: Object.fromEntries(
    STAGES.flatMap((stage) => stage.tasks.map((task) => [`${stage.id}.${task.id}`, [...task.roles]])),
  ),
  fieldRequirements: Object.fromEntries(
    STAGES.flatMap((stage) => stage.fields.map((field) => [`${stage.id}.${field.id}`, Boolean(field.required)])),
  ),
  slaHours: Object.fromEntries(STAGES.map((stage) => [stage.id, stage.slaHours])),
};

function createDefaultWorkersByRole() {
  return Object.fromEntries(ROLES.map((role) => [role.id, []]));
}

let state = loadState();
let currentRoleId = state.currentRoleId || "sales_coordinator";
let selectedOrderId = state.selectedOrderId || state.orders[0]?.id || "";
let selectedStageIndex = 0;
let activeView = "dashboard";
let dashboardFilter = "all";
let specialCalendarCursor = getMalaysiaDate();
let formDraft = {};
let formDraftKey = "";
let purchaseOrderDraft = createBlankPurchaseOrderDraft();
let editingPurchaseOrderId = "";
const SIDEBAR_UI_KEY = "order-workflow-sidebar";
let sidebarCollapsed = localStorage.getItem(SIDEBAR_UI_KEY) !== "expanded";
let chatOrderSearchQuery = "";
let chatSearchQuery = "";
let chatFilter = "all";
let chatReplyToId = "";
let editingChatMessageId = "";
let chatDraftOrderId = "";
let pendingNavigation = null;
let pendingOrderAction = "";
let orderChatDrawerOpen = false;
let remoteSyncReady = false;
let remoteSyncTimer = null;
let applyingRemoteState = false;
let identityClient = null;
let authUser = null;
let authActor = null;
let authMode = "login";
let pendingAuthToken = "";

if (selectedOrderId && !state.orders.some((order) => order.id === selectedOrderId)) {
  selectedOrderId = state.orders[0]?.id || "";
}

const els = {
  authGate: document.getElementById("authGate"),
  authForm: document.getElementById("authForm"),
  authTitle: document.getElementById("authTitle"),
  authHelp: document.getElementById("authHelp"),
  authEmailLabel: document.getElementById("authEmailLabel"),
  authEmail: document.getElementById("authEmail"),
  authPasswordLabel: document.getElementById("authPasswordLabel"),
  authPassword: document.getElementById("authPassword"),
  authConfirmLabel: document.getElementById("authConfirmLabel"),
  authPasswordConfirm: document.getElementById("authPasswordConfirm"),
  authError: document.getElementById("authError"),
  authSubmitBtn: document.getElementById("authSubmitBtn"),
  forgotPasswordBtn: document.getElementById("forgotPasswordBtn"),
  appShell: document.getElementById("appShell"),
  authUserLabel: document.getElementById("authUserLabel"),
  syncStatus: document.getElementById("syncStatus"),
  logoutBtn: document.getElementById("logoutBtn"),
  roleSelect: document.getElementById("roleSelect"),
  sidebarToggleBtn: document.getElementById("sidebarToggleBtn"),
  sidebarToggleIcon: document.getElementById("sidebarToggleIcon"),
  sidebarToggleLabel: document.getElementById("sidebarToggleLabel"),
  orderSelect: document.getElementById("orderSelect"),
  stageNav: document.getElementById("stageNav"),
  roleBadge: document.getElementById("roleBadge"),
  stageTitle: document.getElementById("stageTitle"),
  stageMeta: document.getElementById("stageMeta"),
  systemClock: document.getElementById("systemClock"),
  dashboardTabBtn: document.getElementById("dashboardTabBtn"),
  purchaseOrdersTabBtn: document.getElementById("purchaseOrdersTabBtn"),
  overviewTabBtn: document.getElementById("overviewTabBtn"),
  chatTabBtn: document.getElementById("chatTabBtn"),
  specialViewTabBtn: document.getElementById("specialViewTabBtn"),
  dashboardView: document.getElementById("dashboardView"),
  purchaseOrdersView: document.getElementById("purchaseOrdersView"),
  purchaseOrderCards: document.getElementById("purchaseOrderCards"),
  purchaseOrderEditorPanel: document.getElementById("purchaseOrderEditorPanel"),
  purchaseOrderEditorTitle: document.getElementById("purchaseOrderEditorTitle"),
  purchaseOrderForm: document.getElementById("purchaseOrderForm"),
  purchaseOrderSaveHelp: document.getElementById("purchaseOrderSaveHelp"),
  savePurchaseOrderBtn: document.getElementById("savePurchaseOrderBtn"),
  newPurchaseOrderBtn: document.getElementById("newPurchaseOrderBtn"),
  purchaseOrderTable: document.getElementById("purchaseOrderTable"),
  overviewView: document.getElementById("overviewView"),
  overviewTasks: document.getElementById("overviewTasks"),
  chatView: document.getElementById("chatView"),
  chatUnreadBadge: document.getElementById("chatUnreadBadge"),
  chatOrderSearchInput: document.getElementById("chatOrderSearchInput"),
  chatConversationList: document.getElementById("chatConversationList"),
  chatNotifications: document.getElementById("chatNotifications"),
  chatOrderTitle: document.getElementById("chatOrderTitle"),
  chatOrderSubtitle: document.getElementById("chatOrderSubtitle"),
  chatSearchInput: document.getElementById("chatSearchInput"),
  chatFilterSelect: document.getElementById("chatFilterSelect"),
  chatMessages: document.getElementById("chatMessages"),
  chatTagSelect: document.getElementById("chatTagSelect"),
  chatMessageInput: document.getElementById("chatMessageInput"),
  chatFileInput: document.getElementById("chatFileInput"),
  chatAttachmentSummary: document.getElementById("chatAttachmentSummary"),
  chatReplyPreview: document.getElementById("chatReplyPreview"),
  chatOrderContext: document.getElementById("chatOrderContext"),
  markChatReadBtn: document.getElementById("markChatReadBtn"),
  sendChatBtn: document.getElementById("sendChatBtn"),
  specialView: document.getElementById("specialView"),
  specialCalendarControls: document.getElementById("specialCalendarControls"),
  specialCalendar: document.getElementById("specialCalendar"),
  dashboardCards: document.getElementById("dashboardCards"),
  kanbanBoard: document.getElementById("kanbanBoard"),
  dashboardTable: document.getElementById("dashboardTable"),
  taskDashboardEyebrow: document.getElementById("taskDashboardEyebrow"),
  taskDashboardTitle: document.getElementById("taskDashboardTitle"),
  taskDashboard: document.getElementById("taskDashboard"),
  workflowSlaTimer: document.getElementById("workflowSlaTimer"),
  workflowBypassNotice: document.getElementById("workflowBypassNotice"),
  orderTitle: document.getElementById("orderTitle"),
  orderHeaderMeta: document.getElementById("orderHeaderMeta"),
  openOrderChatBtn: document.getElementById("openOrderChatBtn"),
  cancelOrderBtn: document.getElementById("cancelOrderBtn"),
  deleteOrderBtn: document.getElementById("deleteOrderBtn"),
  gatePill: document.getElementById("gatePill"),
  orderForm: document.getElementById("orderForm"),
  saveBar: document.getElementById("saveBar"),
  saveHelp: document.getElementById("saveHelp"),
  saveDataBtn: document.getElementById("saveDataBtn"),
  taskList: document.getElementById("taskList"),
  completionCount: document.getElementById("completionCount"),
  advanceHelp: document.getElementById("advanceHelp"),
  advanceBtn: document.getElementById("advanceBtn"),
  activityLog: document.getElementById("activityLog"),
  newOrderBtn: document.getElementById("newOrderBtn"),
  adminTabBtn: document.getElementById("adminTabBtn"),
  workflowTabBtn: document.getElementById("workflowTabBtn"),
  workflowView: document.getElementById("workflowView"),
  adminView: document.getElementById("adminView"),
  adminContent: document.getElementById("adminContent"),
  resetAccessBtn: document.getElementById("resetAccessBtn"),
  orderChatDrawer: document.getElementById("orderChatDrawer"),
  drawerChatTitle: document.getElementById("drawerChatTitle"),
  drawerChatMessages: document.getElementById("drawerChatMessages"),
  drawerChatInput: document.getElementById("drawerChatInput"),
  sendDrawerChatBtn: document.getElementById("sendDrawerChatBtn"),
  closeOrderChatBtn: document.getElementById("closeOrderChatBtn"),
  navigationGuard: document.getElementById("navigationGuard"),
  stayOnPageBtn: document.getElementById("stayOnPageBtn"),
  discardAndContinueBtn: document.getElementById("discardAndContinueBtn"),
  saveAndContinueBtn: document.getElementById("saveAndContinueBtn"),
  orderActionDialog: document.getElementById("orderActionDialog"),
  orderActionEyebrow: document.getElementById("orderActionEyebrow"),
  orderActionTitle: document.getElementById("orderActionTitle"),
  orderActionMessage: document.getElementById("orderActionMessage"),
  orderActionConfirmField: document.getElementById("orderActionConfirmField"),
  orderActionConfirmLabel: document.getElementById("orderActionConfirmLabel"),
  orderActionConfirmInput: document.getElementById("orderActionConfirmInput"),
  closeOrderActionBtn: document.getElementById("closeOrderActionBtn"),
  confirmOrderActionBtn: document.getElementById("confirmOrderActionBtn"),
};

function loadState() {
  const stored = canUseRemoteBackend() ? null : localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return normaliseState(JSON.parse(stored));
    } catch (error) {
      console.warn("Stored workflow data could not be loaded", error);
    }
  }

  return normaliseState({
    orders: [],
    access: structuredClone(DEFAULT_ACCESS),
    workersByRole: createDefaultWorkersByRole(),
    chatMessages: [],
    chatReadState: {},
    rawMaterialPurchaseOrders: [],
    currentRoleId: "sales_coordinator",
  });
}

function normaliseState(rawState) {
  const access = rawState.access || {};
  const migratedAccess = {
    roles: { ...structuredClone(DEFAULT_ACCESS.roles), ...(access.roles || {}) },
    stageEditors: { ...structuredClone(DEFAULT_ACCESS.stageEditors), ...(access.stageEditors || {}) },
    stageAdvance: { ...structuredClone(DEFAULT_ACCESS.stageAdvance), ...(access.stageAdvance || {}) },
    taskSigners: { ...structuredClone(DEFAULT_ACCESS.taskSigners), ...(access.taskSigners || {}) },
    fieldRequirements: {
      ...structuredClone(DEFAULT_ACCESS.fieldRequirements),
      ...(access.fieldRequirements || {}),
    },
    slaHours: {
      ...structuredClone(DEFAULT_ACCESS.slaHours),
      ...(access.slaHours || {}),
    },
  };
  ROLES.forEach((role) => {
    migratedAccess.roles[role.id] = {
      ...structuredClone(DEFAULT_ACCESS.roles[role.id]),
      ...(migratedAccess.roles[role.id] || {}),
      canManageAccess: role.id === "ceo",
      canViewSpecial:
        role.id === "ceo" ||
        role.id === "operations_head" ||
        Boolean(migratedAccess.roles[role.id]?.canViewSpecial),
      canViewPurchaseOrders: Boolean(migratedAccess.roles[role.id]?.canViewPurchaseOrders),
      canManagePurchaseOrders: Boolean(migratedAccess.roles[role.id]?.canManagePurchaseOrders),
      canBypassToProcurement: Boolean(migratedAccess.roles[role.id]?.canBypassToProcurement),
    };
  });
  const orders = Array.isArray(rawState.orders) ? rawState.orders.map(migrateOrder) : [];
  const workersByRole = {
    ...createDefaultWorkersByRole(),
    ...(rawState.workersByRole || {}),
  };
  ROLES.forEach((role) => {
    workersByRole[role.id] = Array.isArray(workersByRole[role.id])
      ? workersByRole[role.id].map((name) => String(name).trim()).filter(Boolean)
      : [];
  });

  return {
    orders,
    access: migratedAccess,
    workersByRole,
    chatMessages: Array.isArray(rawState.chatMessages) ? rawState.chatMessages.map(migrateChatMessage) : [],
    chatReadState: rawState.chatReadState && typeof rawState.chatReadState === "object"
      ? rawState.chatReadState
      : {},
    rawMaterialPurchaseOrders: Array.isArray(rawState.rawMaterialPurchaseOrders)
      ? rawState.rawMaterialPurchaseOrders.map(migratePurchaseOrder)
      : [],
    backendMeta: rawState.backendMeta || null,
    currentRoleId: rawState.currentRoleId || "sales_coordinator",
    selectedOrderId: rawState.selectedOrderId || "",
  };
}

function migrateChatMessage(message) {
  return {
    ...message,
    replyToId: String(message?.replyToId || ""),
    editedAt: message?.editedAt || "",
    removedAt: message?.removedAt || "",
    removedBy: message?.removedBy || "",
    files: Array.isArray(message?.files) ? message.files : [],
    tags: Array.isArray(message?.tags) ? message.tags : [],
  };
}

function migrateOrder(order) {
  const now = new Date().toISOString();
  const stageIndex = Number.isInteger(order.stageIndex) ? order.stageIndex : 0;
  const stageEnteredAt = order.stageEnteredAt || order.createdAt || order.log?.[0]?.at || now;
  const stageHistory = Array.isArray(order.stageHistory) && order.stageHistory.length > 0
    ? order.stageHistory
    : [{ stageId: STAGES[Math.min(stageIndex, STAGES.length - 1)].id, enteredAt: stageEnteredAt }];

  return {
    ...order,
    status: order.status === "cancelled" ? "cancelled" : "active",
    cancelledAt: order.cancelledAt || "",
    cancelledBy: String(order.cancelledBy || ""),
    stageIndex,
    fields: order.fields || {},
    checks: order.checks || {},
    log: Array.isArray(order.log) ? order.log : [],
    createdAt: order.createdAt || now,
    stageEnteredAt,
    stageHistory,
    bypasses: Array.isArray(order.bypasses) ? order.bypasses.map(migrateBypass) : [],
  };
}

function migrateBypass(bypass) {
  const stage = STAGES.find((item) => item.id === bypass?.stageId);
  const bypassedAt = bypass?.bypassedAt || new Date().toISOString();
  return {
    stageId: stage?.id || String(bypass?.stageId || ""),
    status: bypass?.status === "resolved" ? "resolved" : "outstanding",
    reason: String(bypass?.reason || "Procurement started before the normal gate was completed."),
    bypassedAt,
    dueAt: bypass?.dueAt || (stage ? getDueDateFromEntry(bypassedAt, stage)?.toISOString() : ""),
    by: String(bypass?.by || "System"),
    resolvedAt: bypass?.resolvedAt || "",
    resolvedBy: bypass?.resolvedBy || "",
  };
}

function migratePurchaseOrder(purchaseOrder) {
  const poDate = String(purchaseOrder?.poDate || "");
  return {
    id: String(purchaseOrder?.id || `RMPO-${Date.now().toString().slice(-8)}`),
    poReference: String(purchaseOrder?.poReference || ""),
    supplier: String(purchaseOrder?.supplier || ""),
    materialDescription: String(purchaseOrder?.materialDescription || ""),
    quantity: String(purchaseOrder?.quantity || ""),
    unit: String(purchaseOrder?.unit || ""),
    poValue: String(purchaseOrder?.poValue || ""),
    poDate,
    eta: String(purchaseOrder?.eta || ""),
    reminderDate: String(purchaseOrder?.reminderDate || calculateDateReminder(poDate, 40)),
    status: ["ordered", "partially-received", "received", "cancelled"].includes(purchaseOrder?.status)
      ? purchaseOrder.status
      : "ordered",
    notes: String(purchaseOrder?.notes || ""),
    createdAt: purchaseOrder?.createdAt || new Date().toISOString(),
    updatedAt: purchaseOrder?.updatedAt || purchaseOrder?.createdAt || new Date().toISOString(),
    createdBy: String(purchaseOrder?.createdBy || "System"),
    updatedBy: String(purchaseOrder?.updatedBy || purchaseOrder?.createdBy || "System"),
  };
}

function saveState() {
  state.currentRoleId = currentRoleId;
  state.selectedOrderId = selectedOrderId;
  if (!canUseRemoteBackend()) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  queueRemoteStateSync();
}

function canUseRemoteBackend() {
  const localDevelopmentHosts = new Set(["localhost", "127.0.0.1", "[::1]"]);
  return (
    typeof fetch === "function" &&
    typeof location !== "undefined" &&
    location.protocol !== "file:" &&
    !localDevelopmentHosts.has(location.hostname)
  );
}

function getAuthenticatedRoleId(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const priority = ["ceo", "operations_head", ...ROLES.map((role) => role.id)];
  return priority.find((roleId) => roles.includes(roleId)) || "";
}

function getAuthenticatedName(user) {
  return String(user?.name || user?.userMetadata?.full_name || user?.email || "Worker").trim();
}

function setAuthMessage(message, tone = "error") {
  els.authError.textContent = message || "";
  els.authError.dataset.tone = tone;
}

function setSyncStatus(message, tone = "") {
  els.syncStatus.textContent = message || "";
  els.syncStatus.dataset.tone = tone;
}

function setAuthMode(mode, token = "") {
  authMode = mode;
  pendingAuthToken = token;
  const isPasswordSetup = mode === "invite" || mode === "recovery";
  els.authEmailLabel.classList.toggle("hidden", isPasswordSetup);
  els.authEmail.required = !isPasswordSetup;
  els.authConfirmLabel.classList.toggle("hidden", !isPasswordSetup);
  els.authPasswordConfirm.required = isPasswordSetup;
  els.authPassword.autocomplete = isPasswordSetup ? "new-password" : "current-password";
  els.authPasswordLabel.textContent = isPasswordSetup ? "New password" : "Password";
  els.forgotPasswordBtn.classList.toggle("hidden", isPasswordSetup);
  els.authTitle.textContent = mode === "invite" ? "Activate your account" : mode === "recovery" ? "Set a new password" : "Sign in";
  els.authHelp.textContent = isPasswordSetup
    ? "Choose a password with at least 8 characters."
    : "Use the company account invited by the CEO.";
  els.authSubmitBtn.textContent = isPasswordSetup ? "Save password" : "Sign in";
  els.authPassword.value = "";
  els.authPasswordConfirm.value = "";
  setAuthMessage("");
}

function renderAuthentication() {
  if (!canUseRemoteBackend()) {
    els.authGate.classList.add("hidden");
    els.appShell.classList.remove("hidden");
    els.authUserLabel.textContent = "Local prototype";
    els.logoutBtn.classList.add("hidden");
    return;
  }

  const authenticated = Boolean(authUser && authActor?.roleId);
  els.authGate.classList.toggle("hidden", authenticated);
  els.appShell.classList.toggle("hidden", !authenticated);
  els.logoutBtn.classList.toggle("hidden", !authenticated);
  els.authUserLabel.textContent = authenticated
    ? `${authActor.name} | ${getRoleTitle(authActor.roleId)}`
    : "";
}

async function finishAuthentication(user) {
  const roleId = getAuthenticatedRoleId(user);
  if (!roleId) {
    await identityClient.logout().catch(() => {});
    authUser = null;
    authActor = null;
    renderAuthentication();
    setAuthMessage("This account has no workflow role. Ask the CEO to assign one in Netlify Identity.");
    return;
  }

  authUser = user;
  authActor = {
    id: user.id,
    email: user.email || "",
    name: getAuthenticatedName(user),
    roleId,
  };
  currentRoleId = roleId;
  state.currentRoleId = roleId;
  setAuthMode("login");
  renderAuthentication();
  render();
  await initialiseRemoteState();
}

async function initialiseIdentity() {
  if (!canUseRemoteBackend()) {
    renderAuthentication();
    render();
    return;
  }

  try {
    identityClient = await import("@netlify/identity");
    const callback = await identityClient.handleAuthCallback();
    if (callback?.type === "invite" && callback.token) {
      setAuthMode("invite", callback.token);
      renderAuthentication();
      return;
    }
    if (callback?.type === "recovery") {
      authUser = callback.user;
      setAuthMode("recovery");
      renderAuthentication();
      return;
    }
    const user = callback?.user || await identityClient.getUser();
    if (user) {
      await finishAuthentication(user);
      return;
    }
    setAuthMode("login");
    renderAuthentication();
  } catch (error) {
    renderAuthentication();
    setAuthMessage(error?.message || "Netlify Identity is not available. Enable Identity for this site before inviting workers.");
  }
}

async function handleAuthSubmit(event) {
  event.preventDefault();
  if (!identityClient) return;
  const password = els.authPassword.value;
  els.authSubmitBtn.disabled = true;
  setAuthMessage("Working...", "info");

  try {
    let user;
    if (authMode === "invite") {
      if (password !== els.authPasswordConfirm.value) throw new Error("The passwords do not match.");
      user = await identityClient.acceptInvite(pendingAuthToken, password);
    } else if (authMode === "recovery") {
      if (password !== els.authPasswordConfirm.value) throw new Error("The passwords do not match.");
      user = await identityClient.updateUser({ password });
    } else {
      user = await identityClient.login(els.authEmail.value.trim(), password);
    }
    await finishAuthentication(user);
  } catch (error) {
    setAuthMessage(error?.message || "Sign-in failed. Check the account details and try again.");
  } finally {
    els.authSubmitBtn.disabled = false;
  }
}

async function handleForgotPassword() {
  const email = els.authEmail.value.trim();
  if (!email) {
    setAuthMessage("Enter the worker email first.");
    els.authEmail.focus();
    return;
  }
  try {
    await identityClient.requestPasswordRecovery(email);
    setAuthMessage("Password reset email sent. Open its link on this site.", "success");
  } catch (error) {
    setAuthMessage(error?.message || "The password reset email could not be sent.");
  }
}

async function handleLogout() {
  if (!identityClient) return;
  await identityClient.logout().catch(() => {});
  authUser = null;
  authActor = null;
  remoteSyncReady = false;
  clearTimeout(remoteSyncTimer);
  state = normaliseState({
    orders: [],
    access: structuredClone(DEFAULT_ACCESS),
    workersByRole: createDefaultWorkersByRole(),
    chatMessages: [],
    chatReadState: {},
    rawMaterialPurchaseOrders: [],
  });
  selectedOrderId = "";
  setSyncStatus("");
  setAuthMode("login");
  renderAuthentication();
}

async function initialiseRemoteState() {
  if (!canUseRemoteBackend() || !authUser) return;

  try {
    const response = await fetch(WORKFLOW_STATE_API, { headers: { accept: "application/json" } });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const backendError = new Error(payload.error || `Backend responded ${response.status}`);
      backendError.status = response.status;
      throw backendError;
    }
    remoteSyncReady = true;
    if (payload.actor) authActor = payload.actor;

    if (payload?.state) {
      applyingRemoteState = true;
      state = normaliseState(payload.state);
      currentRoleId = authActor?.roleId || currentRoleId;
      selectedOrderId = state.selectedOrderId || state.orders[0]?.id || "";
      if (selectedOrderId && !state.orders.some((order) => order.id === selectedOrderId)) {
        selectedOrderId = state.orders[0]?.id || "";
      }
      formDraftKey = "";
      render();
      applyingRemoteState = false;
      setSyncStatus("Saved", "success");
      return;
    }

    syncStateToRemote();
  } catch (error) {
    remoteSyncReady = false;
    applyingRemoteState = false;
    if (error.status === 401) {
      await handleLogout();
      setAuthMessage("Your session expired. Sign in again.");
      return;
    }
    setSyncStatus("Backend unavailable", "error");
    console.warn("Remote workflow backend unavailable.", error);
  }
}

function queueRemoteStateSync() {
  if (!remoteSyncReady || applyingRemoteState || !canUseRemoteBackend()) return;
  clearTimeout(remoteSyncTimer);
  remoteSyncTimer = setTimeout(syncStateToRemote, 600);
}

async function syncStateToRemote() {
  if (!remoteSyncReady || !canUseRemoteBackend() || !authUser) return;
  try {
    setSyncStatus("Saving...", "info");
    const response = await fetch(WORKFLOW_STATE_API, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ state }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const backendError = new Error(payload.error || `Backend responded ${response.status}`);
      backendError.status = response.status;
      throw backendError;
    }
    if (payload?.state) {
      applyingRemoteState = true;
      state = normaliseState(payload.state);
      currentRoleId = payload.actor?.roleId || authActor?.roleId || currentRoleId;
      selectedOrderId = state.orders.some((order) => order.id === selectedOrderId)
        ? selectedOrderId
        : state.orders[0]?.id || "";
      formDraftKey = "";
      render();
      applyingRemoteState = false;
    }
    setSyncStatus("Saved", "success");
  } catch (error) {
    if (error.status === 401) {
      await handleLogout();
      setAuthMessage("Your session expired. Sign in again.");
      return;
    }
    if (error.status === 403 || error.status === 409) {
      await initialiseRemoteState();
    }
    setSyncStatus(error.message || "Save rejected", "error");
    console.warn("Remote workflow sync failed.", error);
  }
}

function createOrder(overrides = {}) {
  const id = `ORD-${Date.now().toString().slice(-6)}`;
  const now = new Date().toISOString();
  return {
    id,
    title: overrides.title || id,
    status: "active",
    cancelledAt: "",
    cancelledBy: "",
    stageIndex: overrides.stageIndex || 0,
    createdAt: overrides.createdAt || now,
    stageEnteredAt: overrides.stageEnteredAt || now,
    stageHistory: overrides.stageHistory || [{ stageId: STAGES[overrides.stageIndex || 0].id, enteredAt: now }],
    fields: {
      orderOwner: getRoleTitle("sales_coordinator"),
      ...overrides.fields,
    },
    checks: overrides.checks || {},
    bypasses: overrides.bypasses || [],
    log: overrides.log || [
      {
        at: now,
        by: overrides.createdBy || "System",
        message: "Order created",
      },
    ],
  };
}

function getOrder() {
  if (selectedOrderId) {
    return state.orders.find((order) => order.id === selectedOrderId) || null;
  }
  return getVisibleOrders()[0] || null;
}

function getCurrentStage(order = getOrder()) {
  if (!order) return STAGES[0];
  selectedStageIndex = Math.min(selectedStageIndex, order.stageIndex);
  return STAGES[selectedStageIndex];
}

function getRoleTitle(roleId) {
  return ROLES.find((role) => role.id === roleId)?.title || roleId;
}

function getRoleAccess(roleId = currentRoleId) {
  return state.access.roles[roleId] || DEFAULT_ACCESS.roles[roleId] || {};
}

function canCreateOrder() {
  return Boolean(getRoleAccess().canCreateOrder || getRoleAccess().canManageAccess);
}

function canManageAccess() {
  return Boolean(getRoleAccess().canManageAccess);
}

function canManageWorkers() {
  return currentRoleId === "ceo" || currentRoleId === "operations_head";
}

function canAccessAdminTab() {
  return canManageAccess() || canManageWorkers();
}

function canViewSpecial() {
  return Boolean(getRoleAccess().canViewSpecial || canManageAccess());
}

function canViewPurchaseOrders() {
  return Boolean(
    getRoleAccess().canViewPurchaseOrders ||
      getRoleAccess().canManagePurchaseOrders ||
      canViewAll(),
  );
}

function canManagePurchaseOrders() {
  return Boolean(getRoleAccess().canManagePurchaseOrders || canManageAccess());
}

function canBypassToProcurement() {
  return Boolean(getRoleAccess().canBypassToProcurement || canManageAccess());
}

function canViewAll() {
  return currentRoleId === "ceo" || currentRoleId === "operations_head";
}

function canCancelOrder() {
  return currentRoleId === "ceo" || currentRoleId === "operations_head";
}

function isOrderCancelled(order) {
  return order?.status === "cancelled";
}

function getPermanentDeleteBlocker(order) {
  if (!order) return "No order is selected.";
  if (currentRoleId !== "ceo") return "Only the CEO can permanently delete an order.";
  if (Number(order.stageIndex) !== 0) return "Only an order that has never advanced beyond Stage 1 can be deleted.";
  if (order.fields?.linkedRawMaterialPoId) return "This order is linked to a raw material PO.";
  const hasEnteredData = Object.entries(order.fields || {}).some(([fieldId, value]) => (
    fieldId !== "orderOwner" && String(value ?? "").trim().length > 0
  ));
  if (hasEnteredData) return "This order contains saved workflow data. Cancel it instead to preserve the record.";
  if (Object.values(order.checks || {}).some((check) => Boolean(check?.done))) {
    return "This order contains a sign-off and cannot be permanently deleted.";
  }
  if ((order.bypasses || []).length > 0) return "This order contains a bypass record.";
  if (state.chatMessages.some((message) => message.orderId === order.id)) {
    return "This order contains chat history and cannot be permanently deleted.";
  }
  const hasActivity = (order.log || []).some((entry) => !/^(Order created|Order cancelled|Order reopened)$/i.test(entry.message || ""));
  if (hasActivity) return "This order contains workflow activity and cannot be permanently deleted.";
  return "";
}

function canEditStage(stage) {
  return Boolean(
    getRoleAccess().canEditAny || (state.access.stageEditors[stage.id] || []).includes(currentRoleId),
  );
}

function canAdvanceStage(stage) {
  return Boolean(
    getRoleAccess().canAdvanceAny || (state.access.stageAdvance[stage.id] || []).includes(currentRoleId),
  );
}

function canSignTask(stage, task) {
  return Boolean(
    getRoleAccess().canEditAny || (state.access.taskSigners[`${stage.id}.${task.id}`] || []).includes(currentRoleId),
  );
}

function canViewStage(stage) {
  return Boolean(
    canViewAll() ||
      canEditStage(stage) ||
      canAdvanceStage(stage) ||
      stage.tasks.some((task) => canSignTask(stage, task)),
  );
}

function getVisibleStageEntries(order = null) {
  return STAGES
    .map((stage, index) => ({ stage, index }))
    .filter(({ stage, index }) => (!order || index <= order.stageIndex) && canViewStage(stage));
}

function getVisibleStageIndexes(order) {
  return getVisibleStageEntries(order).map(({ index }) => index);
}

function getRelevantStageIndex(order, preferredStageId = "") {
  if (!order) return 0;
  const visibleIndexes = getVisibleStageIndexes(order);
  const preferredIndex = STAGES.findIndex((stage) => stage.id === preferredStageId);
  if (preferredIndex >= 0 && visibleIndexes.includes(preferredIndex)) return preferredIndex;
  const bypassIndex = getOutstandingBypasses(order)
    .map((bypass) => STAGES.findIndex((stage) => stage.id === bypass.stageId))
    .find((index) => visibleIndexes.includes(index));
  if (Number.isInteger(bypassIndex)) return bypassIndex;
  if (visibleIndexes.includes(order.stageIndex)) return order.stageIndex;
  return visibleIndexes.at(-1) ?? 0;
}

function canViewOrder(order) {
  return canViewAll() || getVisibleStageIndexes(order).length > 0;
}

function getVisibleOrders() {
  return state.orders.filter(canViewOrder);
}

function getFieldKey(stage, field) {
  return `${stage.id}.${field.id}`;
}

function isFieldRequired(stage, field) {
  return Boolean(state.access.fieldRequirements[getFieldKey(stage, field)]);
}

function isFieldComplete(order, stage, field) {
  return !isFieldRequired(stage, field) || String(order.fields[field.id] || "").trim().length > 0;
}

function isTaskComplete(order, task) {
  return Boolean(order.checks[task.id]?.done);
}

function getGate(stage, order) {
  const missingFields = stage.fields.filter((field) => !isFieldComplete(order, stage, field));
  const missingTasks = stage.tasks.filter((task) => !isTaskComplete(order, task));
  return {
    ready: missingFields.length === 0 && missingTasks.length === 0,
    missingFields,
    missingTasks,
  };
}

function addLog(order, message) {
  order.log.unshift({
    at: new Date().toISOString(),
    by: getRoleTitle(currentRoleId),
    message,
  });
}

function render() {
  normaliseCurrentSelection();
  if (activeView === "special" && !canViewSpecial()) activeView = "dashboard";
  if (activeView === "purchase-orders" && !canViewPurchaseOrders()) activeView = "dashboard";
  if (activeView === "admin" && !canAccessAdminTab()) activeView = "dashboard";
  renderSidebar();
  renderSystemClock();
  renderSelectors();
  renderTabs();
  renderWorkflow();
  renderDashboard();
  renderPurchaseOrders();
  renderOverview();
  renderChat();
  renderSpecialView();
  if (activeView === "admin") renderAdmin();
  renderViewHeader();
  saveState();
}

function renderSidebar() {
  els.appShell.classList.toggle("sidebar-collapsed", sidebarCollapsed);
  els.sidebarToggleBtn.setAttribute("aria-expanded", String(!sidebarCollapsed));
  els.sidebarToggleBtn.title = sidebarCollapsed ? "Extend sidebar" : "Collapse sidebar";
  els.sidebarToggleIcon.innerHTML = sidebarCollapsed ? "&#8250;" : "&#8249;";
  els.sidebarToggleLabel.textContent = sidebarCollapsed ? "Extend" : "Collapse";
}

function normaliseCurrentSelection() {
  const visibleOrders = getVisibleOrders();
  if (selectedOrderId && !visibleOrders.some((order) => order.id === selectedOrderId)) {
    selectedOrderId = visibleOrders[0]?.id || "";
    formDraftKey = "";
  }

  const order = getOrder();
  if (!order) {
    selectedStageIndex = 0;
    return;
  }

  const visibleIndexes = getVisibleStageIndexes(order);
  if (!canViewAll() && !visibleIndexes.includes(selectedStageIndex)) {
    selectedStageIndex = visibleIndexes[0] ?? 0;
    formDraftKey = "";
  }
}

function renderViewHeader() {
  if (activeView === "workflow") {
    const order = getOrder();
    const stage = getCurrentStage(order);
    els.roleBadge.textContent = getRoleTitle(currentRoleId);
    els.stageTitle.textContent = "Orders";
    els.stageMeta.textContent = order && stage
      ? `${order.title} | Stage ${selectedStageIndex + 1}: ${stage.name}`
      : "Select an order to open its workflow.";
  }

  if (activeView === "dashboard") {
    els.roleBadge.textContent = getRoleTitle(currentRoleId);
    els.stageTitle.textContent = "Overview";
    els.stageMeta.textContent = "Important orders, tasks, SLA risks, and material arrivals.";
  }

  if (activeView === "overview") {
    els.roleBadge.textContent = getRoleTitle(currentRoleId);
    els.stageTitle.textContent = "Tasks";
    els.stageMeta.textContent = "Order-neutral list of pending tasks across visible orders.";
  }

  if (activeView === "purchase-orders") {
    els.roleBadge.textContent = getRoleTitle(currentRoleId);
    els.stageTitle.textContent = "Raw Material Purchase Orders";
    els.stageMeta.textContent = "Track stock purchases, expected arrivals, reminders, and customer-order allocation.";
  }

  if (activeView === "chat") {
    els.roleBadge.textContent = getRoleTitle(currentRoleId);
    els.stageTitle.textContent = "Chat";
    els.stageMeta.textContent = "Order conversations, mentions, files, and decisions.";
  }

  if (activeView === "special") {
    els.roleBadge.textContent = getRoleTitle(currentRoleId);
    els.stageTitle.textContent = "Calendar";
    els.stageMeta.textContent = "Calendar view of upcoming order details across all orders.";
  }

  if (activeView === "admin") {
    els.roleBadge.textContent = getRoleTitle(currentRoleId);
    els.stageTitle.textContent = "Admin Access";
    els.stageMeta.textContent = "Adjust authority levels, stage access, sign-off access, and required fields.";
  }
}

function renderSystemClock() {
  els.systemClock.textContent = getMalaysiaTimeLabel();
}

function renderSelectors() {
  const visibleOrders = getVisibleOrders();
  const availableRoles = canUseRemoteBackend()
    ? ROLES.filter((role) => role.id === currentRoleId)
    : ROLES;
  els.roleSelect.innerHTML = availableRoles.map(
    (role) => `<option value="${role.id}" ${role.id === currentRoleId ? "selected" : ""}>${role.title}</option>`,
  ).join("");
  els.roleSelect.disabled = canUseRemoteBackend();

  els.orderSelect.innerHTML = visibleOrders.length
    ? visibleOrders.map((order) => {
        const summary = getOrderSummary(order);
        const label = `${order.title} | ${summary.stage.name} | ${isOrderCancelled(order) ? "Cancelled" : summary.slaLabel}`;
        return `<option value="${order.id}" ${order.id === selectedOrderId ? "selected" : ""}>${escapeHtml(label)}</option>`;
      }).join("")
    : `<option value="">No available orders</option>`;
  els.orderSelect.disabled = visibleOrders.length === 0;

  els.newOrderBtn.disabled = !canCreateOrder();
  els.adminTabBtn.disabled = !canAccessAdminTab();
  els.resetAccessBtn.disabled = !canManageAccess();
  els.specialViewTabBtn.classList.toggle("hidden", !canViewSpecial());
  els.purchaseOrdersTabBtn.classList.toggle("hidden", !canViewPurchaseOrders());
}

function renderTabs() {
  els.appShell.dataset.activeView = activeView;
  els.dashboardView.classList.toggle("hidden", activeView !== "dashboard");
  els.purchaseOrdersView.classList.toggle("hidden", activeView !== "purchase-orders");
  els.overviewView.classList.toggle("hidden", activeView !== "overview");
  els.chatView.classList.toggle("hidden", activeView !== "chat");
  els.specialView.classList.toggle("hidden", activeView !== "special");
  els.workflowView.classList.toggle("hidden", activeView !== "workflow");
  els.adminView.classList.toggle("hidden", activeView !== "admin");
  els.dashboardTabBtn.classList.toggle("active", activeView === "dashboard");
  els.purchaseOrdersTabBtn.classList.toggle("active", activeView === "purchase-orders");
  els.overviewTabBtn.classList.toggle("active", activeView === "overview");
  els.chatTabBtn.classList.toggle("active", activeView === "chat");
  els.specialViewTabBtn.classList.toggle("active", activeView === "special");
  els.workflowTabBtn.classList.toggle("active", activeView === "workflow");
  els.adminTabBtn.classList.toggle("active", activeView === "admin");
}

function createBlankPurchaseOrderDraft() {
  return {
    poReference: "",
    supplier: "",
    materialDescription: "",
    quantity: "",
    unit: "",
    poValue: "",
    poDate: "",
    eta: "",
    reminderDate: "",
    status: "ordered",
    notes: "",
  };
}

function renderPurchaseOrders() {
  if (!canViewPurchaseOrders()) return;
  const purchaseOrders = state.rawMaterialPurchaseOrders;
  const active = purchaseOrders.filter((po) => !["received", "cancelled"].includes(po.status));
  const today = startOfDay(getMalaysiaDate());
  const overdue = active.filter((po) => {
    const eta = parseDateOnly(po.eta);
    return eta && startOfDay(eta) < today;
  });
  const dueSoon = active.filter((po) => {
    const eta = parseDateOnly(po.eta);
    if (!eta) return false;
    const days = Math.ceil((startOfDay(eta) - today) / 864e5);
    return days >= 0 && days <= 7;
  });
  const received = purchaseOrders.filter((po) => po.status === "received");
  const unlinked = purchaseOrders.filter((po) => getLinkedOrdersForPurchaseOrder(po.id).length === 0 && po.status !== "cancelled");

  els.purchaseOrderCards.innerHTML = [
    purchaseOrderMetric("Total POs", purchaseOrders.length, "All raw material purchase orders", "neutral"),
    purchaseOrderMetric("Open", active.length, "Ordered or partially received", active.length ? "warn" : "good"),
    purchaseOrderMetric("Arrival overdue", overdue.length, "ETA has passed", overdue.length ? "bad" : "good"),
    purchaseOrderMetric("Due in 7 days", dueSoon.length, "Expected arrivals approaching", dueSoon.length ? "warn" : "good"),
    purchaseOrderMetric("Received", received.length, "Stock arrival confirmed", "good"),
    purchaseOrderMetric("Unallocated", unlinked.length, "Not linked to a customer order", "neutral"),
  ].join("");

  renderPurchaseOrderEditor();
  if (purchaseOrders.length === 0) {
    els.purchaseOrderTable.innerHTML = `<div class="empty-state"><h3>No raw material POs</h3><p>Create the first stock purchase above.</p></div>`;
    return;
  }

  els.purchaseOrderTable.innerHTML = `
    <div class="overview-table-wrap">
      <table class="overview-table purchase-order-table">
        <thead><tr><th>PO</th><th>Material</th><th>Quantity</th><th>PO date</th><th>Expected arrival</th><th>Reminder</th><th>Status</th><th>Linked orders</th><th>Value</th><th></th></tr></thead>
        <tbody>
          ${purchaseOrders.map(renderPurchaseOrderRow).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function purchaseOrderMetric(label, value, detail, tone) {
  return `<div class="dashboard-card ${tone}"><p>${escapeHtml(label)}</p><strong>${escapeHtml(value)}</strong><span>${escapeHtml(detail)}</span></div>`;
}

function renderPurchaseOrderEditor() {
  const editable = canManagePurchaseOrders();
  els.purchaseOrderEditorPanel.classList.toggle("hidden", !editable);
  if (!editable) return;
  els.purchaseOrderEditorTitle.textContent = editingPurchaseOrderId ? "Edit raw material PO" : "Create raw material PO";
  els.savePurchaseOrderBtn.textContent = editingPurchaseOrderId ? "Save changes" : "Save PO";
  els.purchaseOrderSaveHelp.textContent = editingPurchaseOrderId
    ? "Changes update the shared purchase order register."
    : "Create a PO without waiting for a customer order.";
  els.purchaseOrderForm.querySelectorAll("[data-po-field]").forEach((input) => {
    input.value = purchaseOrderDraft[input.dataset.poField] || "";
  });
}

function renderPurchaseOrderRow(purchaseOrder) {
  const linkedOrders = getLinkedOrdersForPurchaseOrder(purchaseOrder.id);
  const arrivalStatus = getPurchaseOrderArrivalStatus(purchaseOrder);
  return `
    <tr>
      <td><strong>${escapeHtml(purchaseOrder.poReference)}</strong><br><span class="subtle">${escapeHtml(purchaseOrder.supplier)}</span></td>
      <td>${escapeHtml(purchaseOrder.materialDescription)}</td>
      <td>${escapeHtml(purchaseOrder.quantity)} ${escapeHtml(purchaseOrder.unit)}</td>
      <td>${escapeHtml(formatDateDisplay(purchaseOrder.poDate))}</td>
      <td><span class="sla-pill ${arrivalStatus.tone}">${escapeHtml(formatDateDisplay(purchaseOrder.eta))}</span><br><span class="subtle">${escapeHtml(arrivalStatus.label)}</span></td>
      <td>${escapeHtml(formatDateDisplay(purchaseOrder.reminderDate))}</td>
      <td><span class="po-status ${escapeHtml(purchaseOrder.status)}">${escapeHtml(formatPurchaseOrderStatus(purchaseOrder.status))}</span></td>
      <td>${linkedOrders.length ? linkedOrders.map((order) => `<button class="link-button" data-open-order="${order.id}">${escapeHtml(order.title)}</button>`).join("") : '<span class="subtle">Stock / unallocated</span>'}</td>
      <td>${formatCurrency(parseMoney(purchaseOrder.poValue))}</td>
      <td>${canManagePurchaseOrders() ? `<button class="button secondary compact" data-edit-purchase-order="${purchaseOrder.id}">Edit</button>` : ""}</td>
    </tr>
  `;
}

function getLinkedOrdersForPurchaseOrder(purchaseOrderId) {
  return state.orders.filter((order) => order.fields?.linkedRawMaterialPoId === purchaseOrderId);
}

function getPurchaseOrderArrivalStatus(purchaseOrder) {
  if (purchaseOrder.status === "received") return { label: "Received", tone: "completed" };
  if (purchaseOrder.status === "cancelled") return { label: "Cancelled", tone: "neutral" };
  const eta = parseDateOnly(purchaseOrder.eta);
  if (!eta) return { label: "ETA missing", tone: "neutral" };
  const days = Math.ceil((startOfDay(eta) - startOfDay(getMalaysiaDate())) / 864e5);
  if (days < 0) return { label: `${Math.abs(days)} day(s) overdue`, tone: "overdue" };
  if (days <= 7) return { label: `${days} day(s) remaining`, tone: "due-soon" };
  return { label: `${days} day(s) remaining`, tone: "ok" };
}

function formatPurchaseOrderStatus(status) {
  return {
    ordered: "Ordered",
    "partially-received": "Partially received",
    received: "Received",
    cancelled: "Cancelled",
  }[status] || status;
}

function renderDashboard() {
  const visibleOrders = getVisibleOrders();
  const summaries = visibleOrders.map(getOrderSummary);
  const activeSummaries = summaries.filter((summary) => !summary.isCompleted && !summary.isCancelled);
  const overdueCount = activeSummaries.filter((summary) => summary.slaStatus === "overdue").length;
  const dueSoonCount = activeSummaries.filter((summary) => summary.slaStatus === "due-soon").length;
  const blockedCount = activeSummaries.filter((summary) => summary.gate.ready === false || summary.outstandingBypasses.length > 0).length;
  const bypassCount = activeSummaries.filter((summary) => summary.outstandingBypasses.length > 0).length;
  const completedCount = summaries.filter((summary) => summary.isCompleted && !summary.isCancelled).length;
  const cancelledCount = summaries.filter((summary) => summary.isCancelled).length;
  const liveSummaries = summaries.filter((summary) => !summary.isCancelled);
  const totalOrderSize = liveSummaries.reduce((sum, summary) => sum + summary.orderSizeValue, 0);
  const totalDealsClosed = liveSummaries.reduce((sum, summary) => sum + summary.closedDealValue, 0);
  const filteredSummaries = getFilteredSummaries(summaries);
  const visibleStageEntries = getVisibleStageEntries();

  const cards = [
    dashboardCard("Total orders", liveSummaries.length, canViewAll() ? "Active workflow orders" : "Active orders involving your role", "neutral", "all"),
    dashboardCard("Overdue", overdueCount, "Current stage has passed SLA", overdueCount ? "bad" : "good", "overdue"),
    dashboardCard("Due soon", dueSoonCount, "Current stage due within 24 hours", dueSoonCount ? "warn" : "good", "due-soon"),
    dashboardCard("Blocked", blockedCount, "Missing fields or sign-offs", blockedCount ? "warn" : "good", "blocked"),
    dashboardCard("Bypass follow-up", bypassCount, "Skipped gates still require completion", bypassCount ? "bad" : "good", "bypass"),
    dashboardCard("Completed", completedCount, "Finished all required gates", "good", "completed"),
    dashboardCard("Cancelled", cancelledCount, "Archived order records", "neutral", "cancelled"),
  ];
  if (canViewAll()) {
    cards.push(dashboardCard("Total order size", formatCurrency(totalOrderSize), "Quote / PI value in pipeline", "neutral", "all"));
    cards.push(dashboardCard("Deals closed", formatCurrency(totalDealsClosed), "Final payment received", "good", "completed"));
  }
  els.dashboardCards.innerHTML = cards.join("");
  renderKanbanBoard(liveSummaries);

  if (visibleOrders.length === 0) {
    els.dashboardTable.innerHTML = document.getElementById("emptyStateTemplate").innerHTML;
    renderTaskDashboard(summaries);
    return;
  }

  els.dashboardTable.innerHTML = `
    <div class="gantt-board">
      <div class="gantt-header">
        <div class="gantt-order-head">Order</div>
        <div class="gantt-stage-heads">
          ${visibleStageEntries.map(({ stage, index }) => `
            <div class="gantt-stage-head">
              <span>${index + 1}</span>
              <strong>${escapeHtml(stage.name)}</strong>
            </div>
          `).join("")}
        </div>
      </div>
      <div class="gantt-rows">
        ${
          filteredSummaries.length > 0
            ? filteredSummaries.map((summary) => renderGanttRow(summary, visibleStageEntries)).join("")
            : `<div class="empty-state"><h3>No orders in this filter</h3><p>Choose another counter above.</p></div>`
        }
      </div>
    </div>
  `;
  renderTaskDashboard(summaries);
}

function renderKanbanBoard(summaries) {
  const columns = [
    { id: "critical", label: "Critical", tone: "bad", items: [] },
    { id: "action", label: "Action required", tone: "warn", items: [] },
    { id: "due", label: "Due soon", tone: "due", items: [] },
    { id: "track", label: "On track", tone: "good", items: [] },
  ];
  summaries.filter((summary) => !summary.isCompleted).forEach((summary) => {
    const bypassOverdue = summary.outstandingBypasses.some((bypass) => getBypassDueDate(bypass)?.getTime() < Date.now());
    if (summary.slaStatus === "overdue" || bypassOverdue || /Overdue/i.test(summary.poReminderLabel)) {
      columns[0].items.push(summary);
    } else if (!summary.gate.ready || summary.outstandingBypasses.length > 0) {
      columns[1].items.push(summary);
    } else if (summary.slaStatus === "due-soon") {
      columns[2].items.push(summary);
    } else {
      columns[3].items.push(summary);
    }
  });
  els.kanbanBoard.innerHTML = columns.map((column) => `
    <section class="kanban-column ${column.tone}">
      <header><span>${escapeHtml(column.label)}</span><strong>${column.items.length}</strong></header>
      <div class="kanban-cards">
        ${column.items.length ? column.items.map(renderKanbanCard).join("") : '<p class="kanban-empty">Nothing here</p>'}
      </div>
    </section>
  `).join("");
}

function renderKanbanCard(summary) {
  const due = summary.outstandingBypasses.length
    ? getBypassDueDate(summary.outstandingBypasses[0])
    : summary.dueDate;
  return `
    <button class="kanban-card" data-open-order="${summary.order.id}" data-open-stage="${summary.stage.id}">
      <span class="kanban-card-top"><strong>${escapeHtml(summary.order.title)}</strong><em class="sla-pill ${summary.slaStatus}">${escapeHtml(summary.slaLabel)}</em></span>
      <span class="kanban-stage">${summary.stageIndex + 1}. ${escapeHtml(summary.stage.name)}</span>
      <span class="kanban-blocker">${escapeHtml(summary.waitingOn)}</span>
      <span class="kanban-meta"><small>${escapeHtml(summary.responsibleRoles.join(", "))}</small><small>${due ? formatDateTime(due) : "No deadline"}</small></span>
    </button>
  `;
}

function getFilteredSummaries(summaries) {
  if (dashboardFilter === "overdue") return summaries.filter((summary) => !summary.isCompleted && summary.slaStatus === "overdue");
  if (dashboardFilter === "due-soon") return summaries.filter((summary) => !summary.isCompleted && summary.slaStatus === "due-soon");
  if (dashboardFilter === "blocked") return summaries.filter((summary) => !summary.isCompleted && (!summary.gate.ready || summary.outstandingBypasses.length > 0));
  if (dashboardFilter === "bypass") return summaries.filter((summary) => !summary.isCompleted && summary.outstandingBypasses.length > 0);
  if (dashboardFilter === "completed") return summaries.filter((summary) => summary.isCompleted && !summary.isCancelled);
  if (dashboardFilter === "cancelled") return summaries.filter((summary) => summary.isCancelled);
  return summaries.filter((summary) => !summary.isCancelled);
}

function dashboardCard(label, value, detail, tone, filter) {
  return `
    <button class="dashboard-card ${tone} ${dashboardFilter === filter ? "active" : ""}" data-dashboard-filter="${filter}">
      <p>${label}</p>
      <strong>${value}</strong>
      <span>${detail}</span>
    </button>
  `;
}

function renderGanttRow(summary, visibleStageEntries) {
  const rowStageEntries = visibleStageEntries.filter(({ index }) => index <= summary.order.stageIndex || canViewAll());
  return `
    <article class="gantt-row">
      <div class="gantt-order-card">
        <div>
          <strong>${escapeHtml(summary.order.title)}</strong>
          <p>${summary.stageIndex + 1}. ${escapeHtml(summary.stage.name)}</p>
        </div>
        <span class="sla-pill ${summary.slaStatus}">${summary.slaLabel}</span>
        <dl>
          <div class="gantt-detail">
            <dt>Due</dt>
            <dd>${summary.dueDate ? formatDateTime(summary.dueDate) : "Not set"}</dd>
          </div>
          <div class="gantt-detail">
            <dt>Delay</dt>
            <dd>${summary.delayLabel}</dd>
          </div>
          <div class="gantt-detail">
            <dt>Waiting on</dt>
            <dd>${escapeHtml(summary.waitingOn)}</dd>
          </div>
          <div class="gantt-detail">
            <dt>Role</dt>
            <dd>${summary.responsibleRoles.map((role) => escapeHtml(role)).join(", ")}</dd>
          </div>
          <div class="gantt-detail">
            <dt>PO reminder</dt>
            <dd>${escapeHtml(summary.poReminderLabel)}</dd>
          </div>
          <div class="gantt-detail">
            <dt>Bypass</dt>
            <dd>${summary.outstandingBypasses.length ? `${summary.outstandingBypasses.length} gate(s) outstanding` : "None"}</dd>
          </div>
          ${
            canViewAll()
              ? `<div class="gantt-detail">
                  <dt>Order size</dt>
                  <dd>${formatCurrency(summary.orderSizeValue)}</dd>
                </div>
                <div class="gantt-detail">
                  <dt>Closed</dt>
                  <dd>${formatCurrency(summary.closedDealValue)}</dd>
                </div>`
              : ""
          }
        </dl>
        <button class="button secondary compact" data-open-order="${summary.order.id}">Open order</button>
      </div>
      <div class="gantt-stage-track">
        ${rowStageEntries.map(({ stage, index }) => renderGanttStage(summary, stage, index)).join("")}
      </div>
    </article>
  `;
}

function renderGanttStage(summary, stage, index) {
  const bypass = getBypassRecord(summary.order, stage.id);
  const bypassOutstanding = Boolean(bypass && bypass.status !== "resolved");
  const bypassOverdue = bypassOutstanding && getBypassDueDate(bypass)?.getTime() < Date.now();
  const status = bypassOutstanding
    ? `bypass ${bypassOverdue ? "overdue" : "due-soon"}`
    : summary.isCompleted && index <= summary.stageIndex
    ? "complete"
    : index < summary.stageIndex
    ? "complete"
    : index === summary.stageIndex
      ? `current ${summary.slaStatus}`
      : "locked";
  const gateText = bypassOutstanding
    ? "Bypass outstanding"
    : summary.isCompleted && index <= summary.stageIndex
    ? "Complete"
    : index < summary.stageIndex
    ? "Complete"
    : index === summary.stageIndex
      ? summary.gate.ready
        ? "Ready"
        : "Open"
      : "Locked";
  const dueText = bypassOutstanding && getBypassDueDate(bypass)
    ? `Follow-up ${formatDateTime(getBypassDueDate(bypass))}`
    : index === summary.stageIndex && summary.dueDate
    ? formatDateTime(summary.dueDate)
    : `${getStageSlaHours(stage)} hour SLA`;

  return `
    <div class="gantt-stage ${status}">
      <span>${index + 1}</span>
      <strong>${escapeHtml(stage.name)}</strong>
      <small>${escapeHtml(gateText)}</small>
      <em>${escapeHtml(dueText)}</em>
    </div>
  `;
}

function renderTaskDashboard(summaries) {
  const workItems = summaries.flatMap((summary) => getPendingWorkItems(summary.order, summary));

  if (currentRoleId === "operations_head") {
    els.taskDashboardEyebrow.textContent = "COO overview";
    els.taskDashboardTitle.textContent = "Role pending status";
    renderOperationsOverview(workItems);
    return;
  }

  const myTasks = workItems.filter((item) => item.roleIds.includes(currentRoleId));
  els.taskDashboardEyebrow.textContent = "Pending tasks";
  els.taskDashboardTitle.textContent = `${getRoleTitle(currentRoleId)} to-do list`;
  renderRoleTodoList(myTasks);
}

function getPendingWorkItems(order, summary) {
  if (summary.isCompleted || summary.isCancelled) return [];
  const items = [];

  appendStageWorkItems(items, {
    order,
    stage: summary.stage,
    gate: summary.gate,
    dueDate: summary.dueDate,
    enteredAt: order.stageEnteredAt,
    bypass: false,
  });

  summary.outstandingBypasses.forEach((bypass) => {
    const stage = STAGES.find((item) => item.id === bypass.stageId);
    if (!stage || (!canViewAll() && !canViewStage(stage))) return;
    appendStageWorkItems(items, {
      order,
      stage,
      gate: getGate(stage, order),
      dueDate: getBypassDueDate(bypass),
      enteredAt: bypass.bypassedAt,
      bypass: true,
    });
  });

  return items;
}

function appendStageWorkItems(items, { order, stage, gate, dueDate, enteredAt, bypass }) {
  const entered = new Date(enteredAt || "");
  const elapsedHours = Number.isNaN(entered.getTime()) ? 0 : Math.max(0, (Date.now() - entered.getTime()) / 36e5);
  const overdueHours = dueDate ? Math.max(0, (Date.now() - dueDate.getTime()) / 36e5) : 0;
  const prefix = bypass ? "Bypass follow-up: " : "";

  gate.missingFields.forEach((field) => {
    items.push({
      id: `${order.id}.${stage.id}.${field.id}${bypass ? ".bypass" : ""}`,
      type: "field",
      order,
      stage,
      label: `${prefix}Fill required field: ${field.label}`,
      detail: bypass ? "This skipped gate remains mandatory." : "Required order data is missing.",
      roleIds: state.access.stageEditors[stage.id] || [],
      elapsedHours,
      overdueHours,
      dueDate,
    });
  });

  gate.missingTasks.forEach((task) => {
    items.push({
      id: `${order.id}.${stage.id}.${task.id}${bypass ? ".bypass" : ""}`,
      type: "signoff",
      order,
      stage,
      label: `${prefix}${task.label}`,
      detail: bypass ? `Skipped gate: ${task.detail}` : task.detail,
      roleIds: state.access.taskSigners[`${stage.id}.${task.id}`] || task.roles,
      elapsedHours,
      overdueHours,
      dueDate,
    });
  });
}

function renderRoleTodoList(tasks) {
  if (tasks.length === 0) {
    els.taskDashboard.innerHTML = `<div class="empty-state"><h3>No pending tasks</h3><p>All current responsibilities are clear.</p></div>`;
    return;
  }

  els.taskDashboard.innerHTML = `
    <div class="todo-list">
      ${tasks.map(renderTodoItem).join("")}
    </div>
  `;
}

function renderOperationsOverview(workItems) {
  const rows = ROLES
    .filter((role) => role.id !== "ceo")
    .map((role) => {
      const tasks = workItems.filter((item) => item.roleIds.includes(role.id));
      const overdue = tasks.filter((item) => item.overdueHours > 0);
      const longest = tasks.reduce((max, item) => Math.max(max, item.elapsedHours), 0);
      return { role, tasks, overdue, longest };
    })
    .filter((row) => row.tasks.length > 0);

  if (rows.length === 0) {
    els.taskDashboard.innerHTML = `<div class="empty-state"><h3>No team pending tasks</h3><p>All current stage responsibilities are clear.</p></div>`;
    return;
  }

  els.taskDashboard.innerHTML = `
    <div class="overview-table-wrap">
      <table class="overview-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Pending</th>
            <th>Overdue</th>
            <th>Longest open</th>
            <th>Current tasks</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(renderOverviewRow).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderOverviewRow(row) {
  return `
    <tr>
      <td><strong>${escapeHtml(row.role.title)}</strong></td>
      <td>${row.tasks.length}</td>
      <td>${row.overdue.length}</td>
      <td>${formatDuration(row.longest)}</td>
      <td>
        <div class="mini-task-list">
          ${row.tasks.slice(0, 4).map((task) => `
            <button class="mini-task ${task.overdueHours > 0 ? "overdue" : ""}" data-open-order="${task.order.id}" data-open-stage="${task.stage.id}">
              <strong>${escapeHtml(task.order.title)}</strong>
              <span>${escapeHtml(task.label)}</span>
              <em>${task.overdueHours > 0 ? `Overdue ${formatDuration(task.overdueHours)}` : `Open ${formatDuration(task.elapsedHours)}`}</em>
            </button>
          `).join("")}
          ${row.tasks.length > 4 ? `<span class="subtle">+${row.tasks.length - 4} more</span>` : ""}
        </div>
      </td>
    </tr>
  `;
}

function renderTodoItem(task) {
  return `
    <article class="todo-item ${task.overdueHours > 0 ? "overdue" : ""}">
      <div>
        <p class="eyebrow">${escapeHtml(task.stage.name)}</p>
        <h4>${escapeHtml(task.label)}</h4>
        <p>${escapeHtml(task.order.title)} · ${escapeHtml(task.detail)}</p>
      </div>
      <dl>
        <div>
          <dt>Open</dt>
          <dd>${formatDuration(task.elapsedHours)}</dd>
        </div>
        <div>
          <dt>Due</dt>
          <dd>${task.dueDate ? formatDateTime(task.dueDate) : "Not set"}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>${task.overdueHours > 0 ? `Overdue ${formatDuration(task.overdueHours)}` : "On track"}</dd>
        </div>
      </dl>
      <button class="button secondary compact" data-open-order="${task.order.id}" data-open-stage="${task.stage.id}">Open task</button>
    </article>
  `;
}

function getElapsedStageHours(order) {
  if (!order.stageEnteredAt) return 0;
  const entered = new Date(order.stageEnteredAt);
  if (Number.isNaN(entered.getTime())) return 0;
  return Math.max(0, (Date.now() - entered.getTime()) / 36e5);
}

function getOrderSummary(order) {
  const stageIndex = Math.min(order.stageIndex || 0, STAGES.length - 1);
  const stage = STAGES[stageIndex];
  const gate = getGate(stage, order);
  const isCancelled = isOrderCancelled(order);
  const isCompleted = !isCancelled && stageIndex === STAGES.length - 1 && gate.ready;
  const dueDate = getStageDueDate(order, stage);
  const now = new Date();
  const hoursRemaining = dueDate ? (dueDate.getTime() - now.getTime()) / 36e5 : null;
  const slaStatus = isCancelled
    ? "cancelled"
    : isCompleted
    ? "completed"
    : hoursRemaining === null
    ? "neutral"
    : hoursRemaining < 0
      ? "overdue"
      : hoursRemaining <= 24
        ? "due-soon"
        : "ok";
  const slaLabel = {
    overdue: "Overdue",
    "due-soon": "Due soon",
    ok: "On track",
    neutral: "No SLA",
    completed: "Completed",
    cancelled: "Cancelled",
  }[slaStatus];
  const outstandingBypasses = getOutstandingBypasses(order);
  const bypassStage = outstandingBypasses.length
    ? STAGES.find((item) => item.id === outstandingBypasses[0].stageId)
    : null;
  const bypassGate = bypassStage ? getGate(bypassStage, order) : null;
  const waiting = bypassStage && bypassGate
    ? getWaitingOn(bypassStage, order, bypassGate)
    : getWaitingOn(stage, order, gate);
  const poReminderLabel = getPoReminderLabel(order);
  const orderSizeValue = getOrderSizeValue(order);
  const closedDealValue = getClosedDealValue(order);

  return {
    order,
    stage,
    stageIndex,
    gate,
    isCompleted,
    isCancelled,
    dueDate,
    slaStatus,
    slaLabel,
    delayLabel: hoursRemaining !== null && hoursRemaining < 0 ? formatDuration(Math.abs(hoursRemaining)) : "-",
    waitingOn: waiting.label,
    responsibleRoles: waiting.roles,
    poReminderLabel,
    orderSizeValue,
    closedDealValue,
    outstandingBypasses,
  };
}

function getOrderSizeValue(order) {
  return parseMoney(order.fields.piValue) || parseMoney(order.fields.quotationValue);
}

function getClosedDealValue(order) {
  return parseMoney(order.fields.finalPaymentValue);
}

function parseMoney(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-MY", {
    style: "currency",
    currency: "MYR",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function getStageDueDate(order, stage) {
  return getDueDateFromEntry(order.stageEnteredAt, stage);
}

function getDueDateFromEntry(enteredAt, stage) {
  const slaHours = getStageSlaHours(stage);
  if (!slaHours || !enteredAt) return null;
  const entered = new Date(enteredAt);
  if (Number.isNaN(entered.getTime())) return null;
  return adjustWeekendDueDate(new Date(entered.getTime() + slaHours * 36e5));
}

function getBypassRecord(order, stageId) {
  return (order?.bypasses || []).find((bypass) => bypass.stageId === stageId) || null;
}

function getOutstandingBypasses(order) {
  return (order?.bypasses || []).filter((bypass) => bypass.status !== "resolved");
}

function getBypassDueDate(bypass) {
  if (bypass?.dueAt) {
    const due = new Date(bypass.dueAt);
    if (!Number.isNaN(due.getTime())) return due;
  }
  const stage = STAGES.find((item) => item.id === bypass?.stageId);
  return stage ? getDueDateFromEntry(bypass.bypassedAt, stage) : null;
}

function reconcileBypassStatuses(order) {
  let changed = false;
  (order.bypasses || []).forEach((bypass) => {
    const stage = STAGES.find((item) => item.id === bypass.stageId);
    if (!stage) return;
    const ready = getGate(stage, order).ready;
    if (ready && bypass.status !== "resolved") {
      bypass.status = "resolved";
      bypass.resolvedAt = new Date().toISOString();
      bypass.resolvedBy = getRoleTitle(currentRoleId);
      addLog(order, `cleared temporary bypass for "${stage.name}"`);
      changed = true;
    }
    if (!ready && bypass.status === "resolved") {
      bypass.status = "outstanding";
      bypass.resolvedAt = "";
      bypass.resolvedBy = "";
      addLog(order, `reopened temporary bypass follow-up for "${stage.name}"`);
      changed = true;
    }
  });
  return changed;
}

function getStageSlaHours(stage) {
  const configured = Number(state.access.slaHours[stage.id]);
  return Number.isFinite(configured) && configured > 0 ? configured : stage.slaHours;
}

function getRawStageDueDate(order, stage) {
  const slaHours = getStageSlaHours(stage);
  if (!slaHours || !order.stageEnteredAt) return null;
  const entered = new Date(order.stageEnteredAt);
  if (Number.isNaN(entered.getTime())) return null;
  return new Date(entered.getTime() + slaHours * 36e5);
}

function adjustWeekendDueDate(date) {
  const adjusted = new Date(date);
  const malaysiaWeekday = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kuala_Lumpur",
    weekday: "short",
  }).format(adjusted);
  if (malaysiaWeekday === "Sat") adjusted.setTime(adjusted.getTime() + 2 * 864e5);
  if (malaysiaWeekday === "Sun") adjusted.setTime(adjusted.getTime() + 864e5);
  return adjusted;
}

function isWeekendAdjustedDueDate(order, stage) {
  const raw = getRawStageDueDate(order, stage);
  const adjusted = getStageDueDate(order, stage);
  return Boolean(raw && adjusted && raw.getTime() !== adjusted.getTime());
}

function getWaitingOn(stage, order, gate) {
  if (gate.missingFields.length > 0) {
    return {
      label: `Field: ${gate.missingFields[0].label}`,
      roles: (state.access.stageEditors[stage.id] || []).map(getRoleTitle),
    };
  }

  if (gate.missingTasks.length > 0) {
    const task = gate.missingTasks[0];
    return {
      label: `Sign-off: ${task.label}`,
      roles: (state.access.taskSigners[`${stage.id}.${task.id}`] || task.roles).map(getRoleTitle),
    };
  }

  return {
    label: "Ready to advance",
    roles: (state.access.stageAdvance[stage.id] || []).map(getRoleTitle),
  };
}

function getPoReminderLabel(order) {
  const reminder = order.fields.materialStatusReminder;
  if (!reminder) return "Not set";

  const reminderDate = parseDateOnly(reminder);
  if (!reminderDate) return "Not set";

  const today = startOfDay(new Date());
  const days = Math.round((reminderDate.getTime() - today.getTime()) / 864e5);
  if (days < 0) return `Overdue by ${Math.abs(days)} day(s)`;
  if (days === 0) return "Due today";
  return `Due in ${days} day(s)`;
}

function renderOverview() {
  const summaries = getVisibleOrders().map(getOrderSummary);
  const tasks = summaries
    .flatMap((summary) => getPendingWorkItems(summary.order, summary))
    .sort((a, b) => {
      const overdueDelta = b.overdueHours - a.overdueHours;
      if (overdueDelta !== 0) return overdueDelta;
      return b.elapsedHours - a.elapsedHours;
    });

  if (tasks.length === 0) {
    els.overviewTasks.innerHTML = `<div class="empty-state"><h3>No pending tasks</h3><p>No visible orders currently need action.</p></div>`;
    return;
  }

  els.overviewTasks.innerHTML = `
    <div class="todo-list">
      ${tasks.map(renderOverviewTask).join("")}
    </div>
  `;
}

function renderOverviewTask(task) {
  return `
    <article class="todo-item ${task.overdueHours > 0 ? "overdue" : ""}">
      <div>
        <p class="eyebrow">${escapeHtml(task.stage.name)}</p>
        <h4>${escapeHtml(task.label)}</h4>
        <p>${escapeHtml(task.order.title)} · ${task.roleIds.map(getRoleTitle).map(escapeHtml).join(", ")}</p>
      </div>
      <dl>
        <div>
          <dt>Open</dt>
          <dd>${formatDuration(task.elapsedHours)}</dd>
        </div>
        <div>
          <dt>Due</dt>
          <dd>${task.dueDate ? formatDateTime(task.dueDate) : "Not set"}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>${task.overdueHours > 0 ? `Overdue ${formatDuration(task.overdueHours)}` : "Pending"}</dd>
        </div>
      </dl>
      <button class="button secondary compact" data-open-order="${task.order.id}" data-open-stage="${task.stage.id}">Open task</button>
    </article>
  `;
}

function renderChat() {
  renderChatTagOptions();
  renderChatConversationList();
  renderChatNotifications();
  renderChatUnreadBadge();
  const order = getOrder();
  els.chatOrderTitle.textContent = order ? order.title : "Select a conversation";
  els.sendChatBtn.disabled = !order;
  els.chatMessageInput.disabled = !order;
  els.chatFileInput.disabled = !order;
  els.chatTagSelect.disabled = !order;
  els.chatSearchInput.disabled = !order;
  els.chatFilterSelect.disabled = !order;
  els.markChatReadBtn.disabled = !order;
  els.chatSearchInput.value = chatSearchQuery;
  els.chatFilterSelect.value = chatFilter;
  renderChatOrderContext(order);
  renderChatReplyPreview(order);
  if (!order) {
    els.chatOrderSubtitle.textContent = "";
    els.chatMessages.innerHTML = `
      <div class="empty-state">
        <h3>Select an order conversation</h3>
        <p>Choose an order from the conversation list.</p>
      </div>
    `;
    return;
  }

  if (chatDraftOrderId !== order.id && !editingChatMessageId) {
    chatDraftOrderId = order.id;
    els.chatMessageInput.value = getSavedChatDraft(order.id);
  }
  const summary = getOrderSummary(order);
  els.chatOrderSubtitle.textContent = `${summary.stage.name} | ${summary.slaLabel} | ${summary.responsibleRoles.join(", ")}`;
  const messages = getFilteredOrderChatMessages(order.id).slice(-120);
  if (messages.length === 0) {
    els.chatMessages.innerHTML = `
      <div class="empty-state">
        <h3>${getOrderChatMessages(order.id).length ? "No matching messages" : "Start this order conversation"}</h3>
        <p>${getOrderChatMessages(order.id).length ? "Adjust the message search or filter." : "Messages, mentions, and files stay with this order."}</p>
      </div>
    `;
    renderOrderChatDrawer();
    return;
  }

  els.chatMessages.innerHTML = renderChatMessageStream(messages, order);
  els.chatMessages.scrollTop = els.chatMessages.scrollHeight;
  renderOrderChatDrawer();
}

function renderChatNotifications() {
  const notifications = getTaggedMessagesForCurrentRole();
  if (notifications.length === 0) {
    els.chatNotifications.innerHTML = `
      <p class="chat-empty-note">No mentions</p>
    `;
    return;
  }

  els.chatNotifications.innerHTML = notifications.slice(0, 20).map(renderChatNotification).join("");
}

function renderChatNotification(message) {
  const order = state.orders.find((item) => item.id === message.orderId);
  const unread = isChatMessageUnread(message);
  return `
    <button class="chat-notification ${unread ? "unread" : ""}" data-open-chat-order="${order?.id || ""}" data-chat-message-id="${message.id}">
      <span><strong>${escapeHtml(order?.title || "Order not found")}</strong><time>${formatDateTime(message.at)}</time></span>
      <b>${escapeHtml(message.workerName || getRoleTitle(message.roleId))}</b>
      <small>${escapeHtml(message.removedAt ? "Message removed" : message.text || "Attachment")}</small>
    </button>
  `;
}

function getOrderChatMessages(orderId) {
  return state.chatMessages
    .filter((message) => message.orderId === orderId)
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

function getFilteredOrderChatMessages(orderId) {
  const aliases = new Set(getMentionAliasesForRole(currentRoleId));
  const query = chatSearchQuery.trim().toLowerCase();
  return getOrderChatMessages(orderId).filter((message) => {
    const files = Array.isArray(message.files) ? message.files : [];
    if (query && !`${message.text || ""} ${message.workerName || ""} ${files.map((file) => file.name).join(" ")}`.toLowerCase().includes(query)) return false;
    if (chatFilter === "mentions" && !(message.tags || []).some((tag) => aliases.has(tag))) return false;
    if (chatFilter === "files" && files.length === 0) return false;
    if (chatFilter === "unread" && !isChatMessageUnread(message)) return false;
    return true;
  });
}

function getTaggedMessagesForCurrentRole() {
  const aliases = new Set(getMentionAliasesForRole(currentRoleId));
  return state.chatMessages
    .filter((message) => {
      const order = state.orders.find((item) => item.id === message.orderId);
      return order && canViewOrder(order) && (message.tags || []).some((tag) => aliases.has(tag));
    })
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

function renderChatTagOptions() {
  const tagOptions = getChatTagOptions();
  els.chatTagSelect.innerHTML = `
    <option value="">Mention teammate</option>
    ${tagOptions.map((option) => `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`).join("")}
  `;
}

function renderChatConversationList() {
  const query = chatOrderSearchQuery.trim().toLowerCase();
  const conversations = getVisibleOrders()
    .filter((order) => !query || `${order.title} ${order.id}`.toLowerCase().includes(query))
    .map((order) => {
      const messages = getOrderChatMessages(order.id);
      const last = messages.at(-1);
      return { order, last, unread: messages.filter(isChatMessageUnread).length };
    })
    .sort((a, b) => new Date(b.last?.at || b.order.updatedAt || b.order.createdAt).getTime() - new Date(a.last?.at || a.order.updatedAt || a.order.createdAt).getTime());
  els.chatOrderSearchInput.value = chatOrderSearchQuery;
  els.chatConversationList.innerHTML = conversations.length
    ? conversations.map(({ order, last, unread }) => {
        const summary = getOrderSummary(order);
        return `<button class="chat-conversation ${order.id === selectedOrderId ? "active" : ""}" data-chat-order="${order.id}"><span class="conversation-top"><strong>${escapeHtml(order.title)}</strong>${unread ? `<em>${unread}</em>` : ""}</span><span>${escapeHtml(summary.stage.name)} | ${escapeHtml(summary.slaLabel)}</span><small>${escapeHtml(last ? (last.removedAt ? "Message removed" : last.text || last.files?.[0]?.name || "Attachment") : "No messages yet")}</small></button>`;
      }).join("")
    : `<p class="chat-empty-note">No conversations found</p>`;
}

function renderChatMessageStream(messages, order) {
  let previousDay = "";
  let previousAuthor = "";
  return messages.map((message) => {
    const day = formatDateDisplay(new Date(message.at));
    const authorKey = message.authorId || `${message.roleId}:${message.workerName}`;
    const grouped = day === previousDay && authorKey === previousAuthor;
    const separator = day !== previousDay ? `<div class="chat-date-separator"><span>${escapeHtml(day)}</span></div>` : "";
    previousDay = day;
    previousAuthor = authorKey;
    return `${separator}${renderChatMessage(message, order, grouped)}`;
  }).join("");
}

function renderChatMessage(message, order, grouped = false) {
  const files = Array.isArray(message.files) ? message.files : [];
  const reply = message.replyToId ? state.chatMessages.find((item) => item.id === message.replyToId) : null;
  const mine = isOwnChatMessage(message);
  const editable = mine && !message.removedAt && Date.now() - new Date(message.at).getTime() <= 5 * 60 * 1000;
  return `
    <article id="chat-${escapeHtml(message.id)}" class="chat-message ${mine ? "mine" : ""} ${grouped ? "grouped" : ""}">
      ${grouped ? "" : `<div class="chat-avatar">${escapeHtml(getInitials(message.workerName || getRoleTitle(message.roleId)))}</div>`}
      <div class="chat-bubble">
        ${grouped ? "" : `<div class="chat-message-meta"><strong>${escapeHtml(message.workerName || getRoleTitle(message.roleId))}</strong><span>${escapeHtml(getRoleTitle(message.roleId))}</span><time>${formatTimeOnly(message.at)}</time></div>`}
        ${reply ? `<button class="chat-reply-quote" data-jump-message="${reply.id}"><strong>${escapeHtml(reply.workerName || getRoleTitle(reply.roleId))}</strong><span>${escapeHtml(reply.removedAt ? "Message removed" : reply.text || reply.files?.[0]?.name || "Attachment")}</span></button>` : ""}
        ${message.removedAt ? `<p class="removed-message">Message removed by ${escapeHtml(message.removedBy || message.workerName)}</p>` : `<p>${linkifyMentions(escapeHtml(message.text || ""))}</p>`}
        ${files.length && !message.removedAt ? `<div class="chat-files">${files.map((file) => renderChatFile(file, order)).join("")}</div>` : ""}
        <div class="chat-message-footer"><time>${formatTimeOnly(message.at)}${message.editedAt ? " | Edited" : ""}</time><span><button data-reply-message="${message.id}">Reply</button>${editable ? `<button data-edit-message="${message.id}">Edit</button><button data-remove-message="${message.id}">Remove</button>` : ""}</span></div>
      </div>
    </article>
  `;
}

function renderChatFile(file, order) {
  const href = file.key
    ? `${WORKFLOW_STATE_API.replace("workflow-state", "chat-file")}?orderId=${encodeURIComponent(order.id)}&key=${encodeURIComponent(file.key)}`
    : "";
  return href
    ? `<a class="chat-file" href="${href}" target="_blank" rel="noopener"><strong>${escapeHtml(file.name)}</strong><span>${formatFileSize(file.size)}</span></a>`
    : `<span class="chat-file"><strong>${escapeHtml(file.name)}</strong><span>${formatFileSize(file.size)}</span></span>`;
}

function renderChatOrderContext(order) {
  if (!order) {
    els.chatOrderContext.innerHTML = `<p class="chat-empty-note">Select an order for context</p>`;
    return;
  }
  const summary = getOrderSummary(order);
  const tasks = getPendingWorkItems(order, summary).slice(0, 5);
  els.chatOrderContext.innerHTML = `
    <div class="chat-context-header"><p class="eyebrow">Order context</p><h3>${escapeHtml(order.title)}</h3><span class="sla-pill ${summary.slaStatus}">${escapeHtml(summary.slaLabel)}</span></div>
    <dl class="chat-context-list"><div><dt>Stage</dt><dd>${summary.stageIndex + 1}. ${escapeHtml(summary.stage.name)}</dd></div><div><dt>Due</dt><dd>${summary.dueDate ? formatDateTime(summary.dueDate) : "Not set"}</dd></div><div><dt>Waiting on</dt><dd>${escapeHtml(summary.waitingOn)}</dd></div><div><dt>Raw material PO</dt><dd>${escapeHtml(order.fields.rawMaterialPo || "Not linked")}</dd></div></dl>
    <button class="button secondary" data-open-order="${order.id}">Open order</button>
    <div class="chat-context-tasks"><h4>Pending tasks</h4>${tasks.length ? tasks.map((task) => `<button data-open-order="${order.id}" data-open-stage="${task.stage.id}"><strong>${escapeHtml(task.label)}</strong><span>${task.overdueHours > 0 ? `Overdue ${formatDuration(task.overdueHours)}` : escapeHtml(task.stage.name)}</span></button>`).join("") : '<p class="chat-empty-note">No pending tasks</p>'}</div>
  `;
}

function renderChatReplyPreview(order) {
  const editing = editingChatMessageId
    ? state.chatMessages.find((message) => message.id === editingChatMessageId && message.orderId === order?.id)
    : null;
  if (editing) {
    els.chatReplyPreview.classList.remove("hidden");
    els.chatReplyPreview.innerHTML = `<div><strong>Editing your message</strong><span>Changes are limited to five minutes after sending.</span></div><button class="icon-button" data-cancel-edit type="button" aria-label="Cancel edit">X</button>`;
    return;
  }
  const reply = chatReplyToId ? state.chatMessages.find((message) => message.id === chatReplyToId && message.orderId === order?.id) : null;
  if (!reply) {
    els.chatReplyPreview.classList.add("hidden");
    els.chatReplyPreview.innerHTML = "";
    return;
  }
  els.chatReplyPreview.classList.remove("hidden");
  els.chatReplyPreview.innerHTML = `<div><strong>Replying to ${escapeHtml(reply.workerName || getRoleTitle(reply.roleId))}</strong><span>${escapeHtml(reply.removedAt ? "Message removed" : reply.text || reply.files?.[0]?.name || "Attachment")}</span></div><button class="icon-button" data-cancel-reply type="button" aria-label="Cancel reply">X</button>`;
}

function getChatActorKey() {
  return authActor?.id || `local-${currentRoleId}`;
}

function getChatReadMap() {
  const actorKey = getChatActorKey();
  state.chatReadState[actorKey] ||= {};
  return state.chatReadState[actorKey];
}

function isChatMessageUnread(message) {
  if (isOwnChatMessage(message)) return false;
  const lastRead = getChatReadMap()[message.orderId];
  return !lastRead || new Date(message.at).getTime() > new Date(lastRead).getTime();
}

function markOrderChatRead(orderId) {
  if (!orderId) return;
  getChatReadMap()[orderId] = new Date().toISOString();
}

function getUnreadChatCount() {
  return state.chatMessages.filter((message) => {
    const order = state.orders.find((item) => item.id === message.orderId);
    return order && canViewOrder(order) && isChatMessageUnread(message);
  }).length;
}

function renderChatUnreadBadge() {
  const unread = getUnreadChatCount();
  els.chatUnreadBadge.textContent = unread > 99 ? "99+" : String(unread);
  els.chatUnreadBadge.classList.toggle("hidden", unread === 0);
}

function isOwnChatMessage(message) {
  return message.authorId
    ? message.authorId === getChatActorKey()
    : message.roleId === currentRoleId && message.workerName === (authActor?.name || getRoleTitle(currentRoleId));
}

function getInitials(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "OW";
}

function formatTimeOnly(value) {
  return new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kuala_Lumpur", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
}

function getWorkersForRole(roleId) {
  return Array.isArray(state.workersByRole?.[roleId]) ? state.workersByRole[roleId] : [];
}

function getChatTagOptions() {
  return ROLES.flatMap((role) => {
    const roleOption = {
      label: role.title,
      value: makeMention(role.title),
    };
    const workerOptions = getWorkersForRole(role.id).map((name) => ({
      label: `${name} (${role.title})`,
      value: makeMention(name),
    }));
    return [roleOption, ...workerOptions];
  });
}

function getMentionAliasesForRole(roleId) {
  return [makeMention(getRoleTitle(roleId)), ...getWorkersForRole(roleId).map(makeMention)];
}

function makeMention(label) {
  return `@${String(label).trim().replace(/\s+/g, "_")}`;
}

function linkifyMentions(text) {
  return text.replace(/(^|\s)(@[A-Za-z0-9_/-]+)/g, '$1<span class="mention">$2</span>');
}

function formatFileSize(size) {
  const value = Number(size);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1048576) return `${Math.round(value / 1024)} KB`;
  return `${(value / 1048576).toFixed(1)} MB`;
}

function renderSpecialView() {
  if (!canViewSpecial()) {
    els.specialCalendar.innerHTML = "";
    els.specialCalendarControls.innerHTML = "";
    return;
  }

  const monthStart = new Date(specialCalendarCursor.getFullYear(), specialCalendarCursor.getMonth(), 1);
  const monthEnd = new Date(specialCalendarCursor.getFullYear(), specialCalendarCursor.getMonth() + 1, 0);
  const events = getSpecialEvents().filter((event) => event.date >= monthStart && event.date <= monthEnd);
  const eventsByDate = groupEventsByDateMap(events);

  els.specialCalendarControls.innerHTML = renderSpecialCalendarControls();
  els.specialCalendar.innerHTML = `
    <div class="month-calendar">
      <div class="month-weekdays">
        ${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => `<span>${day}</span>`).join("")}
      </div>
      <div class="month-grid">
        ${getMonthCells(monthStart).map((date) => renderMonthCell(date, monthStart, eventsByDate)).join("")}
      </div>
    </div>
  `;
}

function getSpecialEvents() {
  return state.orders.filter((order) => !isOrderCancelled(order)).flatMap((order) => {
    const details = getOverviewOrderDetails(order);
    return [
      overviewEvent(order, "PI issued", "piIssueDate", details),
      overviewEvent(order, "Raw material PO", "rawMaterialPoDate", details),
      overviewEvent(order, "Raw material ETA", "rawMaterialEta", details),
      overviewEvent(order, "Packaging / consumable PO", "packagingPoDate", details),
      overviewEvent(order, "Packaging / consumable ETA", "packagingMaterialEta", details),
      overviewEvent(order, "Estimated shipping", "estimatedShippingDate", details),
    ].filter(Boolean);
  });
}

function overviewEvent(order, label, fieldId, details) {
  const date = parseDateOnly(order.fields[fieldId]);
  if (!date) return null;
  return {
    id: `${order.id}.${fieldId}`,
    order,
    label,
    date,
    details,
  };
}

function getOverviewOrderDetails(order) {
  return {
    value: getOrderSizeValue(order),
    piRef: order.fields.piRef || "-",
    piIssueDate: order.fields.piIssueDate || "-",
    rawMaterialPoDate: order.fields.rawMaterialPoDate || "-",
    packagingPoDate: order.fields.packagingPoDate || "-",
    rawMaterialEta: order.fields.rawMaterialEta || "-",
    packagingMaterialEta: order.fields.packagingMaterialEta || "-",
    estimatedShippingDate: order.fields.estimatedShippingDate || "-",
  };
}

function groupEventsByDate(events) {
  const groups = new Map();
  events.forEach((event) => {
    const key = formatDateInput(event.date);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(event);
  });
  return Array.from(groups.entries()).map(([dateKey, items]) => ({
    dateKey,
    date: parseDateOnly(dateKey),
    items,
  }));
}

function groupEventsByDateMap(events) {
  const groups = new Map();
  events.forEach((event) => {
    const key = formatDateInput(event.date);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(event);
  });
  return groups;
}

function renderSpecialCalendarControls() {
  const months = Array.from({ length: 12 }, (_, index) => {
    const label = new Intl.DateTimeFormat("en-MY", { month: "long" }).format(new Date(2026, index, 1));
    return `<option value="${index}" ${index === specialCalendarCursor.getMonth() ? "selected" : ""}>${label}</option>`;
  }).join("");
  const currentYear = specialCalendarCursor.getFullYear();
  const years = Array.from({ length: 9 }, (_, index) => currentYear - 4 + index)
    .map((year) => `<option value="${year}" ${year === currentYear ? "selected" : ""}>${year}</option>`)
    .join("");

  return `
    <button class="button secondary compact" data-special-month-nav="-1">Previous</button>
    <select class="control compact-control" data-special-month>${months}</select>
    <select class="control compact-control" data-special-year>${years}</select>
    <button class="button secondary compact" data-special-month-nav="1">Next</button>
    <button class="button secondary compact" data-special-today>Today</button>
  `;
}

function getMonthCells(monthStart) {
  const start = new Date(monthStart);
  const mondayIndex = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - mondayIndex);
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

function renderMonthCell(date, monthStart, eventsByDate) {
  const key = formatDateInput(date);
  const events = eventsByDate.get(key) || [];
  const isOutside = date.getMonth() !== monthStart.getMonth();
  const isToday = key === formatDateInput(getMalaysiaDate());

  return `
    <article class="month-cell ${isOutside ? "outside" : ""} ${isToday ? "today" : ""}">
      <div class="month-cell-head">
        <strong>${date.getDate()}</strong>
        ${events.length ? `<span>${events.length}</span>` : ""}
      </div>
      <div class="month-events">
        ${events.map(renderMonthEvent).join("")}
      </div>
    </article>
  `;
}

function renderMonthEvent(event) {
  const color = getOrderColor(event.order.id);
  return `
    <button class="month-event" data-open-order="${event.order.id}" style="--event-color: ${color}">
      <span>${escapeHtml(event.label)}</span>
      <strong>${escapeHtml(event.order.title)}</strong>
      <em>${formatCurrency(event.details.value)} · PI ${escapeHtml(event.details.piRef)}</em>
    </button>
  `;
}

function getOrderColor(orderId) {
  const palette = ["#176b63", "#2c5f9e", "#b56a1d", "#7a4fb3", "#277348", "#b73b3b", "#0f766e", "#9a5b13"];
  let hash = 0;
  for (let index = 0; index < orderId.length; index += 1) {
    hash = (hash * 31 + orderId.charCodeAt(index)) % 9973;
  }
  return palette[hash % palette.length];
}

function renderCalendarDay(group) {
  return `
    <article class="calendar-day">
      <div class="calendar-day-head">
        <strong>${formatCalendarDate(group.date)}</strong>
        <span>${group.items.length} event(s)</span>
      </div>
      <div class="calendar-events">
        ${group.items.map(renderCalendarEvent).join("")}
      </div>
    </article>
  `;
}

function renderCalendarEvent(event) {
  const details = event.details;
  return `
    <button class="calendar-event" data-open-order="${event.order.id}">
      <span>${escapeHtml(event.label)}</span>
      <strong>${escapeHtml(event.order.title)}</strong>
      <dl>
        <div><dt>Value</dt><dd>${formatCurrency(details.value)}</dd></div>
        <div><dt>PI No.</dt><dd>${escapeHtml(details.piRef)}</dd></div>
        <div><dt>PI Date</dt><dd>${escapeHtml(formatDateDisplay(details.piIssueDate))}</dd></div>
        <div><dt>RM PO</dt><dd>${escapeHtml(formatDateDisplay(details.rawMaterialPoDate))}</dd></div>
        <div><dt>PKG PO</dt><dd>${escapeHtml(formatDateDisplay(details.packagingPoDate))}</dd></div>
        <div><dt>RM ETA</dt><dd>${escapeHtml(formatDateDisplay(details.rawMaterialEta))}</dd></div>
        <div><dt>PKG ETA</dt><dd>${escapeHtml(formatDateDisplay(details.packagingMaterialEta))}</dd></div>
        <div><dt>Ship ETA</dt><dd>${escapeHtml(formatDateDisplay(details.estimatedShippingDate))}</dd></div>
      </dl>
    </button>
  `;
}

function formatDuration(hours) {
  if (hours < 24) return `${Math.ceil(hours)} hour(s)`;
  return `${Math.ceil(hours / 24)} day(s)`;
}

function getCountdownState(dueDate) {
  const diffMs = dueDate.getTime() - Date.now();
  const overdue = diffMs < 0;
  const totalSeconds = Math.max(0, Math.floor(Math.abs(diffMs) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const label = days > 0
    ? `${days}d ${hours}h ${minutes}m`
    : `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

  return {
    overdue,
    label: overdue ? `${label} overdue` : label,
  };
}

function renderWorkflow() {
  const order = getOrder();
  if (!order) {
    els.stageNav.innerHTML = "";
    els.orderForm.innerHTML = document.getElementById("emptyStateTemplate").innerHTML;
    els.taskList.innerHTML = "";
    els.activityLog.innerHTML = "";
    els.orderTitle.textContent = "No order selected";
    els.orderHeaderMeta.innerHTML = "";
    els.openOrderChatBtn.disabled = true;
    els.cancelOrderBtn.classList.add("hidden");
    els.deleteOrderBtn.classList.add("hidden");
    els.gatePill.textContent = "No order";
    els.gatePill.className = "status-pill";
    els.completionCount.textContent = "";
    els.advanceHelp.textContent = "Create an order to begin the workflow.";
    els.advanceBtn.disabled = true;
    els.advanceBtn.textContent = "Advance";
    els.stageTitle.textContent = "Workflow";
    els.stageMeta.textContent = "Create a new order to begin.";
    els.saveBar.classList.add("hidden");
    els.workflowSlaTimer.classList.add("hidden");
    els.workflowBypassNotice.classList.add("hidden");
    return;
  }

  if (!selectedOrderId) selectedOrderId = order.id;
  selectedStageIndex = Math.min(selectedStageIndex, order.stageIndex);
  const stage = getCurrentStage(order);
  const gate = getGate(stage, order);
  const roleTitle = getRoleTitle(currentRoleId);
  const completedTasks = stage.tasks.filter((task) => isTaskComplete(order, task)).length;
  if (!canViewAll() && !canViewStage(stage)) {
    const visibleIndexes = getVisibleStageIndexes(order);
    if (visibleIndexes.length === 0) {
      selectedOrderId = "";
      els.stageNav.innerHTML = "";
      els.orderForm.innerHTML = document.getElementById("emptyStateTemplate").innerHTML;
      els.taskList.innerHTML = "";
      els.activityLog.innerHTML = "";
      els.orderTitle.textContent = "No assigned workflow";
      els.orderHeaderMeta.innerHTML = "";
      els.openOrderChatBtn.disabled = true;
      els.cancelOrderBtn.classList.add("hidden");
      els.deleteOrderBtn.classList.add("hidden");
      els.gatePill.textContent = "No assigned order";
      els.gatePill.className = "status-pill";
      els.completionCount.textContent = "";
      els.advanceHelp.textContent = "No current order stage requires this role.";
      els.advanceBtn.disabled = true;
      els.advanceBtn.textContent = "Advance";
      els.stageTitle.textContent = "Workflow";
      els.stageMeta.textContent = "No current order stage requires this role.";
      els.saveBar.classList.add("hidden");
      els.workflowSlaTimer.classList.add("hidden");
      els.workflowBypassNotice.classList.add("hidden");
      return;
    }
    selectedStageIndex = visibleIndexes[0];
    return renderWorkflow();
  }

  els.roleBadge.textContent = roleTitle;
  els.stageTitle.textContent = stage.name;
  els.stageMeta.textContent = `SLA: ${stage.sla}`;
  els.orderTitle.textContent = order.title;
  const cancelled = isOrderCancelled(order);
  const waiting = getWaitingOn(stage, order, gate);
  const dueDate = getStageDueDate(order, stage);
  els.orderHeaderMeta.innerHTML = `
    <span>Stage ${selectedStageIndex + 1} of ${STAGES.length}</span>
    <span>${dueDate ? `Due ${formatDateTime(dueDate)}` : "No SLA deadline"}</span>
    <span>${escapeHtml(waiting.roles.join(", "))}</span>
    ${cancelled ? `<strong>Cancelled ${order.cancelledAt ? formatDateTime(order.cancelledAt) : ""}</strong>` : ""}
    ${getOutstandingBypasses(order).length ? `<strong>${getOutstandingBypasses(order).length} bypass follow-up(s)</strong>` : ""}
  `;
  els.openOrderChatBtn.disabled = false;
  els.cancelOrderBtn.classList.toggle("hidden", !canCancelOrder());
  els.cancelOrderBtn.textContent = cancelled ? "Reopen order" : "Cancel order";
  els.cancelOrderBtn.classList.toggle("danger", !cancelled);
  els.deleteOrderBtn.classList.toggle("hidden", currentRoleId !== "ceo");
  const deleteBlocker = getPermanentDeleteBlocker(order);
  els.deleteOrderBtn.disabled = Boolean(deleteBlocker);
  els.deleteOrderBtn.title = deleteBlocker || "Permanently delete this unused accidental order";
  els.gatePill.textContent = cancelled ? "Cancelled" : gate.ready ? "Gate ready" : "Gate blocked";
  els.gatePill.className = `status-pill ${cancelled ? "cancelled" : gate.ready ? "ready" : "blocked"}`;
  els.completionCount.textContent = `${completedTasks}/${stage.tasks.length} signed off`;
  renderWorkflowCountdown(order, stage, gate);
  renderWorkflowBypassNotice(order, stage);

  renderStageNav(order);
  renderOrderForm(order, stage);
  renderTasks(order, stage);
  renderAdvance(order, stage, gate);
  renderActivity(order);
}

function renderStageNav(order) {
  els.stageNav.innerHTML = STAGES.map((stage, index) => {
    const isOpen = index <= order.stageIndex;
    const isVisible = canViewAll() || canViewStage(stage);
    const clickable = isOpen && isVisible;
    const isActive = clickable && index === selectedStageIndex;
    const stageStatus = getStageNavStatus(order, stage, index);
    return `
      <button class="stage-button ${stageStatus.className} ${isActive ? "active" : ""}"
        data-stage-index="${index}" ${clickable ? "" : "disabled"} aria-disabled="${clickable ? "false" : "true"}
        title="Stage ${index + 1}: ${escapeHtml(stage.name)} - ${escapeHtml(stageStatus.label)}">
        <span class="stage-number">${index + 1}</span>
        <span>
          <span class="stage-name">${stage.name}</span>
          <span class="stage-state">${stageStatus.label}</span>
        </span>
        <span aria-hidden="true">${stageStatus.symbol}</span>
      </button>
    `;
  }).join("");
}

function getStageNavStatus(order, stage, index) {
  if (isOrderCancelled(order)) {
    if (index < order.stageIndex) return { label: "Complete", symbol: "OK", className: "complete-state" };
    if (index > order.stageIndex) return { label: "Locked", symbol: "x", className: "locked-state" };
    return { label: "Cancelled", symbol: "-", className: "cancelled-state" };
  }
  const bypass = getBypassRecord(order, stage.id);
  if (bypass && bypass.status !== "resolved") {
    const overdue = getBypassDueDate(bypass)?.getTime() < Date.now();
    return {
      label: overdue ? "Bypass overdue" : "Bypass outstanding",
      symbol: "!",
      className: overdue ? "overdue-state" : "pending-state bypass-state",
    };
  }

  if (index < order.stageIndex) {
    return { label: "Complete", symbol: "OK", className: "complete-state" };
  }

  if (index > order.stageIndex) {
    return { label: "Locked", symbol: "x", className: "locked-state" };
  }

  const dueDate = getStageDueDate(order, stage);
  const overdue = dueDate && dueDate.getTime() < Date.now();
  const gate = getGate(stage, order);
  if (overdue && !gate.ready) {
    return { label: "Overdue", symbol: "!", className: "overdue-state" };
  }

  if (gate.ready && index === STAGES.length - 1) {
    return { label: "Complete", symbol: "OK", className: "complete-state" };
  }

  return { label: "Pending", symbol: ">", className: "pending-state" };
}

function renderWorkflowBypassNotice(order, stage) {
  const outstanding = getOutstandingBypasses(order);
  const canStartBypass = order.stageIndex === 2 && selectedStageIndex === 2 && canBypassToProcurement();
  if (outstanding.length === 0 && !canStartBypass) {
    els.workflowBypassNotice.classList.add("hidden");
    els.workflowBypassNotice.innerHTML = "";
    return;
  }

  els.workflowBypassNotice.classList.remove("hidden");
  if (outstanding.length > 0) {
    els.workflowBypassNotice.innerHTML = `
      <div>
        <p class="eyebrow">Temporary bypass follow-up</p>
        <h3>${outstanding.length} skipped gate(s) still require completion</h3>
        <p class="subtle">Stage 5 may be prepared now, but the order cannot advance to production until every skipped gate is cleared.</p>
      </div>
      <div class="bypass-items">
        ${outstanding.map((bypass) => {
          const bypassStage = STAGES.find((item) => item.id === bypass.stageId);
          const due = getBypassDueDate(bypass);
          const overdue = due && due.getTime() < Date.now();
          const visible = bypassStage && (canViewAll() || canViewStage(bypassStage));
          return `<button class="bypass-item ${overdue ? "overdue" : ""}" data-bypass-stage="${bypassStage ? STAGES.indexOf(bypassStage) : ""}" ${visible ? "" : "disabled"}><strong>${escapeHtml(bypassStage?.name || bypass.stageId)}</strong><span>${overdue ? "Overdue" : "Due"} ${due ? formatDateTime(due) : "not set"}</span><small>${escapeHtml(bypass.reason)}</small></button>`;
        }).join("")}
      </div>
    `;
    return;
  }

  els.workflowBypassNotice.innerHTML = `
    <div>
      <p class="eyebrow">Early raw material procurement</p>
      <h3>Temporarily bypass Stages 3 and 4</h3>
      <p class="subtle">Use this only when raw material must be ordered before deposit and technical sign-off. Both gates remain mandatory follow-up tasks.</p>
    </div>
    <label class="bypass-reason"><span>Reason for bypass</span><textarea id="bypassReasonInput" class="text-area" placeholder="Explain why procurement must begin early."></textarea></label>
    <button id="bypassToProcurementBtn" class="button danger" type="button">Proceed temporarily to Stage 5</button>
  `;
}

function renderWorkflowCountdown(order, stage, gate) {
  if (isOrderCancelled(order)) {
    els.workflowSlaTimer.classList.remove("hidden", "overdue", "ready");
    els.workflowSlaTimer.innerHTML = `
      <div>
        <p class="eyebrow">Order archived</p>
        <h3>SLA timer stopped</h3>
        <p class="subtle">This cancelled order is read only until it is reopened.</p>
      </div>
      <strong>Stopped</strong>
    `;
    return;
  }
  const isAdvancedStage = selectedStageIndex < order.stageIndex;
  const bypass = getBypassRecord(order, stage.id);
  if (bypass && bypass.status !== "resolved") {
    const bypassDue = getBypassDueDate(bypass);
    if (!bypassDue) {
      els.workflowSlaTimer.classList.add("hidden");
      return;
    }
    const countdown = getCountdownState(bypassDue);
    els.workflowSlaTimer.classList.remove("hidden", "ready");
    els.workflowSlaTimer.classList.toggle("overdue", countdown.overdue);
    els.workflowSlaTimer.innerHTML = `
      <div>
        <p class="eyebrow">Bypass follow-up SLA</p>
        <h3>${countdown.overdue ? "Skipped gate overdue" : "Skipped gate time remaining"}</h3>
        <p class="subtle">Due ${formatDateTime(bypassDue)}. Complete this gate to clear the bypass flag.</p>
      </div>
      <strong>${countdown.label}</strong>
    `;
    return;
  }
  const dueDate = getStageDueDate(order, stage);
  if (!dueDate) {
    els.workflowSlaTimer.classList.add("hidden");
    return;
  }

  if (isAdvancedStage) {
    els.workflowSlaTimer.classList.remove("hidden", "overdue");
    els.workflowSlaTimer.classList.add("ready");
    els.workflowSlaTimer.innerHTML = `
      <div>
        <p class="eyebrow">SLA timer stopped</p>
        <h3>Stage advanced</h3>
        <p class="subtle">This stage moved forward. Due was ${formatDateTime(dueDate)}</p>
      </div>
      <strong>Stopped</strong>
    `;
    return;
  }

  const countdown = getCountdownState(dueDate);
  const adjustedText = isWeekendAdjustedDueDate(order, stage)
    ? "Weekend adjusted"
    : "SLA deadline";

  els.workflowSlaTimer.classList.remove("hidden", "overdue", "ready");
  els.workflowSlaTimer.classList.toggle("overdue", countdown.overdue);
  els.workflowSlaTimer.classList.toggle("ready", gate.ready && !countdown.overdue);
  els.workflowSlaTimer.innerHTML = `
    <div>
      <p class="eyebrow">${adjustedText}</p>
      <h3>${countdown.overdue ? "SLA overdue" : "SLA time remaining"}</h3>
      <p class="subtle">Due ${formatDateTime(dueDate)}</p>
    </div>
    <strong>${countdown.label}</strong>
  `;
}

function renderOrderForm(order, stage) {
  const editable = canEditStage(stage) && !isOrderCancelled(order);
  initialiseFormDraft(order, stage);
  els.orderForm.innerHTML = stage.fields
    .map((field) => {
      const value = escapeHtml(formDraft[field.id] || "");
      const fieldType = FIELD_TYPES[field.type] || "text";
      const required = isFieldRequired(stage, field);
      const complete = !required || String(order.fields[field.id] || "").trim().length > 0;
      const fieldEditable = editable && !field.generated && (field.type !== "po-select" || canViewPurchaseOrders());
      const lockedHint = field.generated ? `<span class="lock-hint">Auto calculated</span>` : editable ? "" : `<span class="lock-hint">Read only</span>`;
      const requiredHint = required ? (complete ? "Required" : "Required missing") : "Optional";
      let input;
      if (field.type === "po-select") {
        input = `
          <select class="text-input" data-field="${field.id}" ${fieldEditable ? "" : "disabled"}>
            <option value="">No stock PO linked</option>
            ${state.rawMaterialPurchaseOrders
              .filter((purchaseOrder) => purchaseOrder.status !== "cancelled" || purchaseOrder.id === formDraft[field.id])
              .map((purchaseOrder) => `<option value="${escapeHtml(purchaseOrder.id)}" ${purchaseOrder.id === formDraft[field.id] ? "selected" : ""}>${escapeHtml(purchaseOrder.poReference)} | ${escapeHtml(purchaseOrder.materialDescription)} | ${escapeHtml(formatDateDisplay(purchaseOrder.eta))}</option>`)
              .join("")}
          </select>
        `;
      } else if (field.type === "long") {
        input = `<textarea class="text-area" data-field="${field.id}" ${fieldEditable ? "" : "disabled"}>${value}</textarea>`;
      } else {
        input = `<input class="text-input" type="${fieldType}" data-field="${field.id}" value="${value}" ${fieldEditable ? "" : "disabled"} />`;
      }
      return `
        <div class="form-field ${field.type === "long" ? "full" : ""}">
          <label>
            <span>${field.label}</span>
            <span class="lock-hint">${requiredHint}</span>
          </label>
          ${input}
          ${lockedHint}
        </div>
      `;
    })
    .join("");
  renderSaveState();
}

function initialiseFormDraft(order, stage) {
  const key = getDraftKey(order, stage);
  if (formDraftKey === key) return;

  formDraftKey = key;
  formDraft = Object.fromEntries(stage.fields.map((field) => [field.id, order.fields[field.id] || ""]));
}

function getDraftKey(order, stage) {
  return `${order.id}.${stage.id}`;
}

function hasUnsavedChanges(order, stage) {
  if (formDraftKey !== getDraftKey(order, stage)) return false;
  return stage.fields.some((field) => String(formDraft[field.id] || "") !== String(order.fields[field.id] || ""));
}

function renderSaveState() {
  const order = getOrder();
  const stage = getCurrentStage(order);
  if (!order || !stage) return;

  const editable = canEditStage(stage) && !isOrderCancelled(order);
  const unsaved = hasUnsavedChanges(order, stage);
  els.saveBar.classList.toggle("hidden", !editable);
  els.saveDataBtn.disabled = !editable || !unsaved;
  els.saveHelp.textContent = editable
    ? unsaved
      ? "Unsaved changes must be saved before this order can move forward."
      : "All visible form data is saved."
    : isOrderCancelled(order)
      ? "Cancelled orders are read only until reopened."
      : "This role can view this data but cannot amend it.";
}

function renderTasks(order, stage) {
  els.taskList.innerHTML = stage.tasks
    .map((task) => {
      const check = order.checks[task.id];
      const done = Boolean(check?.done);
      const allowed = canSignTask(stage, task) && !isOrderCancelled(order);
      const owners = (state.access.taskSigners[`${stage.id}.${task.id}`] || task.roles)
        .map((roleId) => `<span class="role-chip">${getRoleTitle(roleId)}</span>`)
        .join("");
      return `
        <article class="task-row ${done ? "done" : ""}">
          <div class="task-main">
            <div>
              <h4>${task.label}</h4>
              <p>${task.detail}</p>
            </div>
            <span class="task-status ${done ? "done" : "blocked"}">${done ? "Signed off" : "Pending"}</span>
          </div>
          <div class="task-actions">
            <div class="task-owner">${owners}</div>
            <button class="button ${done ? "ghost" : "primary"}" data-task="${task.id}" ${allowed ? "" : "disabled"}>
              ${done ? "Reopen" : "Sign off"}
            </button>
          </div>
          ${
            done
              ? `<p class="subtle">Completed by ${escapeHtml(check.by)} on ${formatDateTime(check.at)}</p>`
              : ""
          }
        </article>
      `;
    })
    .join("");
}

function renderAdvance(order, stage, gate) {
  const isLastStage = order.stageIndex === STAGES.length - 1 && selectedStageIndex === STAGES.length - 1;
  const atOpenStage = selectedStageIndex === order.stageIndex;
  const allowed = canAdvanceStage(stage);
  const unsaved = hasUnsavedChanges(order, stage);
  const outstandingBypasses = getOutstandingBypasses(order);
  const bypassBlocked = atOpenStage && order.stageIndex >= 4 && outstandingBypasses.length > 0;
  const cancelled = isOrderCancelled(order);
  const canAdvance = gate.ready && allowed && atOpenStage && !isLastStage && !unsaved && !bypassBlocked && !cancelled;
  const missingText = gate.ready
    ? "All required details and sign-offs are complete."
    : `${gate.missingFields.length} field(s) and ${gate.missingTasks.length} sign-off(s) still required.`;
  const roleText = allowed ? "Your role can move this stage forward." : "Your role cannot move this stage forward.";
  const unsavedText = unsaved ? " Save the current form data first." : "";
  const bypassText = bypassBlocked ? ` Complete ${outstandingBypasses.length} bypassed gate(s) before production.` : "";

  els.advanceHelp.textContent = cancelled
    ? "This order is cancelled and cannot advance unless it is reopened."
    : isLastStage ? "This order has reached the closing stage." : `${missingText} ${roleText}${unsavedText}${bypassText}`;
  els.advanceBtn.disabled = !canAdvance;
  const nextStage = STAGES[order.stageIndex + 1];
  els.advanceBtn.textContent = isLastStage ? "Closed" : nextStage ? `Advance to ${nextStage.name}` : "Advance";
}

function renderActivity(order) {
  els.activityLog.innerHTML = order.log
    .slice(0, 20)
    .map((entry) => `<li><strong>${escapeHtml(entry.by)}</strong> ${escapeHtml(entry.message)} <span>${formatDateTime(entry.at)}</span></li>`)
    .join("");
}

function renderAdmin() {
  els.resetAccessBtn.classList.toggle("hidden", !canManageAccess());
  const workerRows = renderWorkerRows();

  if (!canManageAccess()) {
    els.adminContent.innerHTML = `
      ${workerRows}
      <div class="admin-section">
        <h4>Authority controls</h4>
        <p class="subtle">Only the CEO role can change workflow authority, SLA timing, required fields, and sign-off access.</p>
      </div>
    `;
    return;
  }

  const roleAccessRows = ROLES.map((role) => {
    const access = getRoleAccess(role.id);
    return `
      <div class="matrix-row">
        <div class="matrix-cell"><strong>${role.title}</strong></div>
        ${permissionCell(role.id, "canCreateOrder", "Create orders", access.canCreateOrder)}
        ${permissionCell(role.id, "canManageAccess", "Manage access", access.canManageAccess)}
        ${permissionCell(role.id, "canViewSpecial", "Special View", access.canViewSpecial)}
        ${permissionCell(role.id, "canViewPurchaseOrders", "View raw POs", access.canViewPurchaseOrders)}
        ${permissionCell(role.id, "canManagePurchaseOrders", "Manage raw POs", access.canManagePurchaseOrders)}
        ${permissionCell(role.id, "canBypassToProcurement", "Bypass to Stage 5", access.canBypassToProcurement)}
        ${permissionCell(role.id, "canEditAny", "Edit any stage", access.canEditAny)}
        ${permissionCell(role.id, "canAdvanceAny", "Advance any stage", access.canAdvanceAny)}
      </div>
    `;
  }).join("");

  const stageRows = STAGES.map((stage) => `
    <div class="admin-section">
      <h4>${stage.name}</h4>
      <div class="sla-setting-row">
        <label>
          <span>SLA hours for this stage</span>
          <input class="text-input" type="number" min="0.01" step="0.01" data-sla-stage="${stage.id}" value="${getStageSlaHours(stage)}" />
        </label>
        <p class="subtle">For quick testing, use small values like 0.01 hour, which is about 36 seconds.</p>
      </div>
      <div class="matrix">
        <div class="matrix-row">
          <div class="matrix-cell header">Role</div>
          <div class="matrix-cell header">Can amend data</div>
          <div class="matrix-cell header">Can advance stage</div>
        </div>
        ${ROLES.map((role) => `
          <div class="matrix-row">
            <div class="matrix-cell">${role.title}</div>
            ${stageAccessCell(stage.id, role.id, "stageEditors", "Edit")}
            ${stageAccessCell(stage.id, role.id, "stageAdvance", "Advance")}
          </div>
        `).join("")}
      </div>
      <div class="admin-section">
        <h4>Required fields</h4>
        <div class="matrix">
          ${stage.fields.map((field) => requiredFieldRow(stage, field)).join("")}
        </div>
      </div>
      <div class="admin-section">
        <h4>Task sign-off access</h4>
        ${stage.tasks.map((task) => taskAccessBlock(stage, task)).join("")}
      </div>
    </div>
  `).join("");

  els.adminContent.innerHTML = `
    ${workerRows}
    <div class="admin-section">
      <h4>Global permissions</h4>
      <div class="matrix">
        <div class="matrix-row">
          <div class="matrix-cell header">Role</div>
          <div class="matrix-cell header">Create orders</div>
          <div class="matrix-cell header">Manage access</div>
          <div class="matrix-cell header">Special View</div>
          <div class="matrix-cell header">View raw POs</div>
          <div class="matrix-cell header">Manage raw POs</div>
          <div class="matrix-cell header">Bypass to Stage 5</div>
          <div class="matrix-cell header">Edit any stage</div>
          <div class="matrix-cell header">Advance any stage</div>
        </div>
        ${roleAccessRows}
      </div>
    </div>
    ${stageRows}
  `;
}

function renderWorkerRows() {
  return `
    <div class="admin-section">
      <h4>Worker names by role</h4>
      <p class="subtle">Login accounts are invited in Netlify Identity. Assign the matching role key there; CEO and COO can maintain display names here for chat tagging.</p>
      <div class="worker-role-list">
        ${ROLES.map((role) => {
          const names = getWorkersForRole(role.id).join("\n");
          return `
            <label class="worker-role-row">
              <span>${role.title}<small>Identity role: ${role.id}</small></span>
              <textarea class="text-area" data-worker-role="${role.id}" ${canManageWorkers() ? "" : "disabled"}>${escapeHtml(names)}</textarea>
            </label>
          `;
        }).join("")}
      </div>
    </div>
  `;
}

function permissionCell(roleId, key, label, checked) {
  return `
    <div class="matrix-cell">
      <label>
        <input type="checkbox" data-global-role="${roleId}" data-global-key="${key}" ${checked ? "checked" : ""} />
        <span>${label}</span>
      </label>
    </div>
  `;
}

function stageAccessCell(stageId, roleId, accessKey, label) {
  const checked = (state.access[accessKey][stageId] || []).includes(roleId);
  return `
    <div class="matrix-cell">
      <label>
        <input type="checkbox" data-stage-access="${accessKey}" data-stage-id="${stageId}" data-role-id="${roleId}" ${checked ? "checked" : ""} />
        <span>${label}</span>
      </label>
    </div>
  `;
}

function requiredFieldRow(stage, field) {
  const checked = isFieldRequired(stage, field);
  return `
    <div class="matrix-row">
      <div class="matrix-cell"><strong>${field.label}</strong></div>
      <div class="matrix-cell">
        <label>
          <input type="checkbox" data-required-field="${getFieldKey(stage, field)}" ${checked ? "checked" : ""} />
          <span>Required before advance</span>
        </label>
      </div>
    </div>
  `;
}

function taskAccessBlock(stage, task) {
  const taskKey = `${stage.id}.${task.id}`;
  const signers = state.access.taskSigners[taskKey] || [];
  return `
    <div class="task-row">
      <div class="task-main">
        <div>
          <h4>${task.label}</h4>
          <p>${task.detail}</p>
        </div>
      </div>
      <div class="task-owner">
        ${ROLES.map((role) => `
          <label class="role-chip">
            <input type="checkbox" data-task-access="${taskKey}" data-role-id="${role.id}" ${signers.includes(role.id) ? "checked" : ""} />
            <span>${role.title}</span>
          </label>
        `).join("")}
      </div>
    </div>
  `;
}

function handleFieldInput(event) {
  const fieldId = event.target.dataset.field;
  if (!fieldId) return;

  const order = getOrder();
  const stage = getCurrentStage(order);
  if (!canEditStage(stage)) return;

  formDraft[fieldId] = event.target.value;
  if (fieldId === "linkedRawMaterialPoId") {
    applyLinkedPurchaseOrderToDraft(event.target.value);
  }
  if (fieldId === "rawMaterialPoDate") {
    formDraft.materialStatusReminder = calculateDateReminder(event.target.value, 40);
    const reminderInput = els.orderForm.querySelector('[data-field="materialStatusReminder"]');
    if (reminderInput) reminderInput.value = formDraft.materialStatusReminder || "";
  }
  if (fieldId === "packagingPoDate") {
    formDraft.packagingStatusReminder = calculateDateReminder(event.target.value, 14);
    const reminderInput = els.orderForm.querySelector('[data-field="packagingStatusReminder"]');
    if (reminderInput) reminderInput.value = formDraft.packagingStatusReminder || "";
  }
  renderSaveState();
  renderAdvance(order, stage, getGate(stage, order));
}

function applyLinkedPurchaseOrderToDraft(purchaseOrderId) {
  const purchaseOrder = state.rawMaterialPurchaseOrders.find((item) => item.id === purchaseOrderId);
  if (!purchaseOrder) return;
  const mappedFields = {
    rawMaterialPo: purchaseOrder.poReference,
    rawMaterialPoDate: purchaseOrder.poDate,
    rawMaterialEta: purchaseOrder.eta,
    materialStatusReminder: purchaseOrder.reminderDate || calculateDateReminder(purchaseOrder.poDate, 40),
  };
  Object.entries(mappedFields).forEach(([fieldId, value]) => {
    formDraft[fieldId] = value || "";
    const input = els.orderForm.querySelector(`[data-field="${fieldId}"]`);
    if (input) input.value = formDraft[fieldId];
  });
}

function saveStageData() {
  const order = getOrder();
  const stage = getCurrentStage(order);
  if (!order || !stage || !canEditStage(stage) || isOrderCancelled(order)) return false;
  initialiseFormDraft(order, stage);

  if (stage.id === "procurement_readiness") {
    formDraft.materialStatusReminder = calculateDateReminder(formDraft.rawMaterialPoDate, 40);
    formDraft.packagingStatusReminder = calculateDateReminder(formDraft.packagingPoDate, 14);
  }

  const changedFields = stage.fields.filter(
    (field) => String(order.fields[field.id] || "") !== String(formDraft[field.id] || ""),
  );
  if (changedFields.length === 0) {
    renderSaveState();
    return true;
  }

  changedFields.forEach((field) => {
    order.fields[field.id] = formDraft[field.id] || "";
  });

  if (formDraft.customerRef?.trim()) {
    order.title = `${formDraft.customerRef.trim()} / ${order.id}`;
  }

  addLog(order, `saved ${changedFields.length} field(s) on "${stage.name}"`);
  reconcileBypassStatuses(order);
  formDraftKey = "";
  render();
  return true;
}

function handlePurchaseOrderInput(event) {
  const fieldId = event.target.dataset.poField;
  if (!fieldId || !canManagePurchaseOrders()) return;
  purchaseOrderDraft[fieldId] = event.target.value;
  if (fieldId === "poDate") {
    purchaseOrderDraft.reminderDate = calculateDateReminder(event.target.value, 40);
    const reminderInput = els.purchaseOrderForm.querySelector('[data-po-field="reminderDate"]');
    if (reminderInput) reminderInput.value = purchaseOrderDraft.reminderDate;
  }
}

function resetPurchaseOrderDraft() {
  editingPurchaseOrderId = "";
  purchaseOrderDraft = createBlankPurchaseOrderDraft();
  renderPurchaseOrders();
}

function editPurchaseOrder(purchaseOrderId) {
  const purchaseOrder = state.rawMaterialPurchaseOrders.find((item) => item.id === purchaseOrderId);
  if (!purchaseOrder || !canManagePurchaseOrders()) return;
  editingPurchaseOrderId = purchaseOrder.id;
  purchaseOrderDraft = { ...createBlankPurchaseOrderDraft(), ...purchaseOrder };
  renderPurchaseOrders();
  els.purchaseOrderEditorPanel.scrollIntoView({ behavior: "smooth", block: "start" });
}

function savePurchaseOrder() {
  if (!canManagePurchaseOrders()) return false;
  const requiredFields = ["poReference", "supplier", "materialDescription", "quantity", "unit", "poDate", "eta"];
  const missingField = requiredFields.find((fieldId) => !String(purchaseOrderDraft[fieldId] || "").trim());
  if (missingField) {
    const input = els.purchaseOrderForm.querySelector(`[data-po-field="${missingField}"]`);
    input?.focus();
    els.purchaseOrderSaveHelp.textContent = "Complete all required PO fields before saving.";
    return false;
  }

  const now = new Date().toISOString();
  const existing = state.rawMaterialPurchaseOrders.find((item) => item.id === editingPurchaseOrderId);
  const record = migratePurchaseOrder({
    ...existing,
    ...purchaseOrderDraft,
    id: existing?.id || `RMPO-${Date.now()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`,
    reminderDate: calculateDateReminder(purchaseOrderDraft.poDate, 40),
    createdAt: existing?.createdAt || now,
    createdBy: existing?.createdBy || getRoleTitle(currentRoleId),
    updatedAt: now,
    updatedBy: getRoleTitle(currentRoleId),
  });

  if (existing) {
    Object.assign(existing, record);
  } else {
    state.rawMaterialPurchaseOrders.unshift(record);
  }
  editingPurchaseOrderId = "";
  purchaseOrderDraft = createBlankPurchaseOrderDraft();
  render();
  return true;
}

async function sendChatMessage() {
  const order = getOrder();
  if (!order) return;
  const text = els.chatMessageInput.value.trim();
  const selectedFiles = Array.from(els.chatFileInput.files || []);
  if (!text && selectedFiles.length === 0) return;

  if (editingChatMessageId) {
    const message = state.chatMessages.find((item) => item.id === editingChatMessageId);
    if (!message || !isOwnChatMessage(message) || message.removedAt) return;
    message.text = text;
    message.tags = Array.from(new Set(text.match(/@[A-Za-z0-9_/-]+/g) || []));
    message.editedAt = new Date().toISOString();
    editingChatMessageId = "";
    chatReplyToId = "";
    els.chatMessageInput.value = "";
    els.sendChatBtn.textContent = "Send";
    saveChatDraft(order.id, "");
    render();
    return;
  }

  els.sendChatBtn.disabled = true;
  els.sendChatBtn.textContent = selectedFiles.length ? "Uploading" : "Sending";
  let files;
  try {
    files = await uploadChatFiles(order.id, selectedFiles);
  } catch (error) {
    els.chatAttachmentSummary.textContent = error.message || "Attachment upload failed";
    els.sendChatBtn.disabled = false;
    els.sendChatBtn.textContent = "Send";
    return;
  }

  const workerName = authActor?.name || getRoleTitle(currentRoleId);
  const tags = Array.from(new Set(text.match(/@[A-Za-z0-9_/-]+/g) || []));
  state.chatMessages.push({
    id: `MSG-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
    orderId: order.id,
    at: new Date().toISOString(),
    roleId: currentRoleId,
    authorId: getChatActorKey(),
    workerName,
    text,
    tags,
    files,
    replyToId: chatReplyToId,
    editedAt: "",
    removedAt: "",
    removedBy: "",
  });

  els.chatMessageInput.value = "";
  els.chatFileInput.value = "";
  els.chatAttachmentSummary.textContent = "";
  chatReplyToId = "";
  saveChatDraft(order.id, "");
  markOrderChatRead(order.id);
  els.sendChatBtn.disabled = false;
  els.sendChatBtn.textContent = "Send";
  render();
}

async function uploadChatFiles(orderId, files) {
  if (files.length > 3) throw new Error("Attach up to 3 files per message.");
  const oversized = files.find((file) => file.size > 4 * 1024 * 1024);
  if (oversized) throw new Error(`${oversized.name} exceeds the 4 MB attachment limit.`);
  if (!canUseRemoteBackend()) {
    return files.map((file) => ({ name: file.name, size: file.size, type: file.type || "application/octet-stream", key: "" }));
  }
  const uploaded = [];
  for (const file of files) {
    const data = await readFileAsDataUrl(file);
    const response = await fetch(WORKFLOW_STATE_API.replace("workflow-state", "chat-file"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ orderId, name: file.name, type: file.type || "application/octet-stream", data }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || `Could not upload ${file.name}.`);
    uploaded.push(payload.file);
  }
  return uploaded;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function beginChatReply(messageId) {
  const order = getOrder();
  const message = state.chatMessages.find((item) => item.id === messageId && item.orderId === order?.id);
  if (!message) return;
  chatReplyToId = message.id;
  editingChatMessageId = "";
  els.sendChatBtn.textContent = "Send";
  renderChatReplyPreview(order);
  els.chatMessageInput.focus();
}

function beginChatEdit(messageId) {
  const message = state.chatMessages.find((item) => item.id === messageId);
  if (!message || !isOwnChatMessage(message) || message.removedAt) return;
  editingChatMessageId = message.id;
  chatReplyToId = "";
  els.chatMessageInput.value = message.text || "";
  els.sendChatBtn.textContent = "Save edit";
  renderChatReplyPreview(getOrder());
  els.chatMessageInput.focus();
}

function removeChatMessage(messageId) {
  const message = state.chatMessages.find((item) => item.id === messageId);
  if (!message || !isOwnChatMessage(message) || message.removedAt) return;
  message.text = "";
  message.tags = [];
  message.removedAt = new Date().toISOString();
  message.removedBy = authActor?.name || getRoleTitle(currentRoleId);
  render();
}

function getChatDraftStorage() {
  try {
    return JSON.parse(localStorage.getItem(CHAT_DRAFTS_KEY) || "{}");
  } catch {
    return {};
  }
}

function getChatDraftKey(orderId) {
  return `${getChatActorKey()}:${orderId}`;
}

function getSavedChatDraft(orderId) {
  return getChatDraftStorage()[getChatDraftKey(orderId)] || "";
}

function saveChatDraft(orderId, value) {
  if (!orderId) return;
  const drafts = getChatDraftStorage();
  const key = getChatDraftKey(orderId);
  if (value) drafts[key] = value;
  else delete drafts[key];
  localStorage.setItem(CHAT_DRAFTS_KEY, JSON.stringify(drafts));
}

function renderOrderChatDrawer() {
  els.orderChatDrawer.classList.toggle("hidden", !orderChatDrawerOpen);
  if (!orderChatDrawerOpen) return;
  const order = getOrder();
  els.drawerChatTitle.textContent = order?.title || "No order";
  if (!order) {
    els.drawerChatMessages.innerHTML = `<p class="chat-empty-note">No order selected</p>`;
    return;
  }
  const messages = getOrderChatMessages(order.id).slice(-30);
  els.drawerChatMessages.innerHTML = messages.length
    ? messages.map((message) => `<article class="drawer-message ${isOwnChatMessage(message) ? "mine" : ""}"><span><strong>${escapeHtml(message.workerName || getRoleTitle(message.roleId))}</strong><time>${formatTimeOnly(message.at)}</time></span><p>${escapeHtml(message.removedAt ? "Message removed" : message.text || message.files?.[0]?.name || "Attachment")}</p></article>`).join("")
    : `<p class="chat-empty-note">No messages yet</p>`;
  els.drawerChatMessages.scrollTop = els.drawerChatMessages.scrollHeight;
}

function sendDrawerChatMessage() {
  const order = getOrder();
  const text = els.drawerChatInput.value.trim();
  if (!order || !text) return;
  state.chatMessages.push({
    id: `MSG-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
    orderId: order.id,
    at: new Date().toISOString(),
    roleId: currentRoleId,
    authorId: getChatActorKey(),
    workerName: authActor?.name || getRoleTitle(currentRoleId),
    text,
    tags: Array.from(new Set(text.match(/@[A-Za-z0-9_/-]+/g) || [])),
    files: [],
    replyToId: "",
    editedAt: "",
    removedAt: "",
    removedBy: "",
  });
  els.drawerChatInput.value = "";
  markOrderChatRead(order.id);
  render();
}

function handleChatTagChange(event) {
  const mention = event.target.value;
  if (!mention) return;
  const currentText = els.chatMessageInput.value.trimEnd();
  els.chatMessageInput.value = currentText ? `${currentText} ${mention} ` : `${mention} `;
  event.target.value = "";
  els.chatMessageInput.focus();
}

function calculateDateReminder(dateValue, days) {
  const parsed = parseDateOnly(dateValue);
  if (!parsed) return "";
  return formatDateInput(addDays(parsed, days));
}

function getFieldLabel(stage, fieldId) {
  return stage.fields.find((field) => field.id === fieldId)?.label || fieldId;
}

function handleTaskClick(event) {
  const taskId = event.target.dataset.task;
  if (!taskId) return;

  const order = getOrder();
  const stage = getCurrentStage(order);
  const task = stage.tasks.find((item) => item.id === taskId);
  if (!task || !canSignTask(stage, task) || isOrderCancelled(order)) return;

  const current = order.checks[taskId]?.done;
  if (current) {
    order.checks[taskId] = {
      done: false,
      by: getRoleTitle(currentRoleId),
      at: new Date().toISOString(),
    };
    addLog(order, `reopened "${task.label}"`);
  } else {
    order.checks[taskId] = {
      done: true,
      by: getRoleTitle(currentRoleId),
      at: new Date().toISOString(),
    };
    addLog(order, `signed off "${task.label}"`);
  }
  reconcileBypassStatuses(order);
  render();
}

function bypassToProcurement() {
  const order = getOrder();
  if (!order || isOrderCancelled(order) || order.stageIndex !== 2 || selectedStageIndex !== 2 || !canBypassToProcurement()) return;
  const reasonInput = document.getElementById("bypassReasonInput");
  const reason = reasonInput?.value.trim() || "";
  if (!reason) {
    reasonInput?.focus();
    return;
  }

  const bypassedAt = new Date().toISOString();
  order.bypasses = [2, 3].map((stageIndex) => {
    const stage = STAGES[stageIndex];
    return {
      stageId: stage.id,
      status: "outstanding",
      reason,
      bypassedAt,
      dueAt: getDueDateFromEntry(bypassedAt, stage)?.toISOString() || "",
      by: getRoleTitle(currentRoleId),
      resolvedAt: "",
      resolvedBy: "",
    };
  });
  order.stageIndex = 4;
  selectedStageIndex = 4;
  order.stageEnteredAt = bypassedAt;
  order.stageHistory.push({
    stageId: STAGES[4].id,
    enteredAt: bypassedAt,
    bypassedStageIds: [STAGES[2].id, STAGES[3].id],
  });
  formDraftKey = "";
  addLog(order, `temporarily bypassed Stages 3 and 4 to begin procurement: ${reason}`);
  render();
}

function advanceOrder() {
  const order = getOrder();
  const stage = getCurrentStage(order);
  const gate = getGate(stage, order);
  if (
    isOrderCancelled(order) ||
    !gate.ready ||
    !canAdvanceStage(stage) ||
    selectedStageIndex !== order.stageIndex ||
    (order.stageIndex >= 4 && getOutstandingBypasses(order).length > 0)
  ) return;

  order.stageIndex += 1;
  selectedStageIndex = order.stageIndex;
  order.stageEnteredAt = new Date().toISOString();
  order.stageHistory.push({ stageId: STAGES[order.stageIndex].id, enteredAt: order.stageEnteredAt });
  formDraftKey = "";
  addLog(order, `advanced order to "${STAGES[order.stageIndex].name}"`);
  render();
}

function handleAdminChange(event) {
  const target = event.target;
  if (target.dataset.workerRole) {
    if (!canManageWorkers()) return;
    state.workersByRole[target.dataset.workerRole] = target.value
      .split(/[\n,]+/)
      .map((name) => name.trim())
      .filter(Boolean);
    render();
    return;
  }

  if (!canManageAccess()) return;

  if (target.dataset.globalRole) {
    const roleId = target.dataset.globalRole;
    const key = target.dataset.globalKey;
    state.access.roles[roleId][key] = target.checked;
  }

  if (target.dataset.stageAccess) {
    toggleArrayItem(
      state.access[target.dataset.stageAccess][target.dataset.stageId],
      target.dataset.roleId,
      target.checked,
    );
  }

  if (target.dataset.taskAccess) {
    toggleArrayItem(state.access.taskSigners[target.dataset.taskAccess], target.dataset.roleId, target.checked);
  }

  if (target.dataset.requiredField) {
    state.access.fieldRequirements[target.dataset.requiredField] = target.checked;
  }

  if (target.dataset.slaStage) {
    const nextValue = Number(target.value);
    state.access.slaHours[target.dataset.slaStage] = Number.isFinite(nextValue) && nextValue > 0 ? nextValue : DEFAULT_ACCESS.slaHours[target.dataset.slaStage];
  }

  normaliseCurrentSelection();
  render();
}

function toggleArrayItem(array, value, shouldInclude) {
  const index = array.indexOf(value);
  if (shouldInclude && index === -1) array.push(value);
  if (!shouldInclude && index !== -1) array.splice(index, 1);
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kuala_Lumpur",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
  return `${time} & ${formatDateDisplay(date)}`;
}

function getMalaysiaTimeLabel(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kuala_Lumpur",
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour12: false,
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("hour")}:${part("minute")} & ${formatDateDisplay(new Date(Number(part("year")), Number(part("month")) - 1, Number(part("day"))))}`;
}

function parseDateOnly(value) {
  if (!value) return null;
  const parts = String(value).split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const parsed = new Date(parts[0], parts[1] - 1, parts[2]);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMalaysiaDate(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kuala_Lumpur",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return new Date(Number(part("year")), Number(part("month")) - 1, Number(part("day")));
}

function formatCalendarDate(date) {
  const weekday = new Intl.DateTimeFormat("en-MY", { weekday: "short" }).format(date);
  return `${weekday}, ${formatDateDisplay(date)}`;
}

function formatDateDisplay(value) {
  if (!value || value === "-") return "-";
  const parsed = value instanceof Date ? value : parseDateOnly(value);
  if (!parsed) return value;
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = new Intl.DateTimeFormat("en-MY", { month: "long" }).format(parsed);
  const year = parsed.getFullYear();
  return `${day}-${month}-${year}`;
}

function hasUnsavedPurchaseOrderChanges() {
  if (!canManagePurchaseOrders()) return false;
  const existing = state.rawMaterialPurchaseOrders.find((item) => item.id === editingPurchaseOrderId);
  const baseline = existing || createBlankPurchaseOrderDraft();
  return Object.keys(createBlankPurchaseOrderDraft()).some(
    (key) => String(purchaseOrderDraft[key] || "") !== String(baseline[key] || ""),
  );
}

function getUnsavedContext() {
  const order = getOrder();
  const stage = order ? getCurrentStage(order) : null;
  if (order && stage && hasUnsavedChanges(order, stage)) return "stage";
  if (activeView === "purchase-orders" && hasUnsavedPurchaseOrderChanges()) return "purchase-order";
  return "";
}

function requestNavigation(action) {
  if (!getUnsavedContext()) {
    action();
    return;
  }
  pendingNavigation = action;
  els.navigationGuard.classList.remove("hidden");
}

function closeNavigationGuard() {
  pendingNavigation = null;
  els.navigationGuard.classList.add("hidden");
  renderSelectors();
}

function completePendingNavigation(mode) {
  const action = pendingNavigation;
  if (!action) return closeNavigationGuard();
  const context = getUnsavedContext();
  if (mode === "save") {
    const saved = context === "purchase-order" ? savePurchaseOrder() : saveStageData();
    if (!saved) return;
  } else if (mode === "discard") {
    if (context === "purchase-order") {
      editingPurchaseOrderId = "";
      purchaseOrderDraft = createBlankPurchaseOrderDraft();
    } else {
      formDraftKey = "";
      formDraft = {};
    }
  }
  pendingNavigation = null;
  els.navigationGuard.classList.add("hidden");
  action();
}

function openOrderActionDialog(action) {
  const order = getOrder();
  if (!order) return;
  const deleting = action === "delete";
  pendingOrderAction = action;
  els.orderActionEyebrow.textContent = deleting ? "Permanent deletion" : "Order status";
  els.orderActionTitle.textContent = deleting
    ? `Delete ${order.id}?`
    : isOrderCancelled(order) ? "Reopen this order?" : "Cancel this order?";
  els.orderActionMessage.textContent = deleting
    ? `This permanently removes ${order.title}. This action cannot be undone.`
    : isOrderCancelled(order)
      ? `${order.title} will return to its current workflow stage and its SLA timer will restart.`
      : `${order.title} will be archived, its SLA timer will stop, and its workflow will become read only.`;
  els.orderActionConfirmField.classList.toggle("hidden", !deleting);
  els.orderActionConfirmLabel.textContent = deleting ? `Type ${order.id} to confirm` : "";
  els.orderActionConfirmInput.value = "";
  els.confirmOrderActionBtn.textContent = deleting
    ? "Delete permanently"
    : isOrderCancelled(order) ? "Reopen order" : "Cancel and archive";
  els.confirmOrderActionBtn.className = `button ${deleting || !isOrderCancelled(order) ? "danger" : "primary"}`;
  els.confirmOrderActionBtn.disabled = deleting;
  els.orderActionDialog.classList.remove("hidden");
  if (deleting) els.orderActionConfirmInput.focus();
}

function closeOrderActionDialog() {
  pendingOrderAction = "";
  els.orderActionDialog.classList.add("hidden");
  els.orderActionConfirmInput.value = "";
}

function performOrderAction() {
  const order = getOrder();
  if (!order || !pendingOrderAction) return closeOrderActionDialog();
  if (pendingOrderAction === "delete") {
    if (getPermanentDeleteBlocker(order) || els.orderActionConfirmInput.value.trim() !== order.id) return;
    const deletedOrderId = order.id;
    state.orders = state.orders.filter((item) => item.id !== deletedOrderId);
    state.chatMessages = state.chatMessages.filter((message) => message.orderId !== deletedOrderId);
    Object.values(state.chatReadState || {}).forEach((readState) => delete readState[deletedOrderId]);
    if (canUseRemoteBackend()) state.deletedOrderIds = [...new Set([...(state.deletedOrderIds || []), deletedOrderId])];
    selectedOrderId = getVisibleOrders()[0]?.id || "";
    selectedStageIndex = 0;
    formDraftKey = "";
    closeOrderActionDialog();
    render();
    return;
  }

  if (!canCancelOrder()) return closeOrderActionDialog();
  const reopening = isOrderCancelled(order);
  order.status = reopening ? "active" : "cancelled";
  order.cancelledAt = reopening ? "" : new Date().toISOString();
  order.cancelledBy = reopening ? "" : getRoleTitle(currentRoleId);
  if (!canUseRemoteBackend()) addLog(order, reopening ? "Order reopened" : "Order cancelled");
  if (reopening) {
    order.stageEnteredAt = new Date().toISOString();
    order.stageHistory.push({ stageId: STAGES[order.stageIndex].id, enteredAt: order.stageEnteredAt, reopened: true });
  }
  formDraftKey = "";
  closeOrderActionDialog();
  render();
}

function openOrderWorkspace(orderId, preferredStageId = "") {
  selectedOrderId = orderId;
  const order = getOrder();
  selectedStageIndex = getRelevantStageIndex(order, preferredStageId);
  formDraftKey = "";
  activeView = "workflow";
  render();
}

function openOrderConversation(orderId, messageId = "") {
  selectedOrderId = orderId;
  const order = getOrder();
  selectedStageIndex = getRelevantStageIndex(order);
  formDraftKey = "";
  chatReplyToId = "";
  editingChatMessageId = "";
  chatDraftOrderId = "";
  activeView = "chat";
  markOrderChatRead(orderId);
  render();
  if (messageId) document.getElementById(`chat-${messageId}`)?.scrollIntoView({ block: "center" });
}

function switchView(view) {
  activeView = view;
  render();
}

els.roleSelect.addEventListener("change", (event) => {
  if (canUseRemoteBackend()) return;
  const nextRole = event.target.value;
  requestNavigation(() => {
    currentRoleId = nextRole;
    formDraftKey = "";
    if (!canAccessAdminTab() && activeView === "admin") activeView = "workflow";
    render();
  });
});

els.sidebarToggleBtn.addEventListener("click", () => {
  sidebarCollapsed = !sidebarCollapsed;
  localStorage.setItem(SIDEBAR_UI_KEY, sidebarCollapsed ? "collapsed" : "expanded");
  renderSidebar();
});

els.orderSelect.addEventListener("change", (event) => {
  requestNavigation(() => openOrderWorkspace(event.target.value));
});

els.stageNav.addEventListener("click", (event) => {
  const button = event.target.closest("[data-stage-index]");
  if (!button || button.disabled) return;
  const nextStageIndex = Number(button.dataset.stageIndex);
  const order = getOrder();
  if (!order || nextStageIndex > order.stageIndex || !canViewStage(STAGES[nextStageIndex])) return;
  requestNavigation(() => {
    selectedStageIndex = nextStageIndex;
    formDraftKey = "";
    render();
  });
});

els.dashboardCards.addEventListener("click", (event) => {
  const card = event.target.closest("[data-dashboard-filter]");
  if (!card) return;
  dashboardFilter = card.dataset.dashboardFilter;
  render();
});

els.orderForm.addEventListener("input", handleFieldInput);
els.saveDataBtn.addEventListener("click", saveStageData);
els.taskList.addEventListener("click", handleTaskClick);
els.advanceBtn.addEventListener("click", advanceOrder);
els.dashboardTable.addEventListener("click", (event) => {
  const button = event.target.closest("[data-open-order]");
  if (!button) return;
  requestNavigation(() => openOrderWorkspace(button.dataset.openOrder, button.dataset.openStage));
});

els.kanbanBoard.addEventListener("click", (event) => {
  const button = event.target.closest("[data-open-order]");
  if (!button) return;
  requestNavigation(() => openOrderWorkspace(button.dataset.openOrder, button.dataset.openStage));
});

els.taskDashboard.addEventListener("click", (event) => {
  const button = event.target.closest("[data-open-order]");
  if (!button) return;
  requestNavigation(() => openOrderWorkspace(button.dataset.openOrder, button.dataset.openStage));
});

els.newOrderBtn.addEventListener("click", () => {
  if (!canCreateOrder()) return;
  requestNavigation(() => {
    const order = createOrder({ createdBy: getRoleTitle(currentRoleId) });
    state.orders.unshift(order);
    selectedOrderId = order.id;
    selectedStageIndex = 0;
    activeView = "workflow";
    render();
  });
});

els.adminTabBtn.addEventListener("click", () => {
  if (!canAccessAdminTab()) return;
  requestNavigation(() => switchView("admin"));
});

els.dashboardTabBtn.addEventListener("click", () => {
  requestNavigation(() => switchView("dashboard"));
});

els.purchaseOrdersTabBtn.addEventListener("click", () => {
  if (!canViewPurchaseOrders()) return;
  requestNavigation(() => switchView("purchase-orders"));
});

els.purchaseOrderForm.addEventListener("input", handlePurchaseOrderInput);
els.purchaseOrderForm.addEventListener("change", handlePurchaseOrderInput);
els.savePurchaseOrderBtn.addEventListener("click", savePurchaseOrder);
els.newPurchaseOrderBtn.addEventListener("click", resetPurchaseOrderDraft);
els.purchaseOrderTable.addEventListener("click", (event) => {
  const editButton = event.target.closest("[data-edit-purchase-order]");
  if (editButton) {
    editPurchaseOrder(editButton.dataset.editPurchaseOrder);
    return;
  }
  const orderButton = event.target.closest("[data-open-order]");
  if (!orderButton) return;
  requestNavigation(() => openOrderWorkspace(orderButton.dataset.openOrder, STAGES[4].id));
});

els.chatTabBtn.addEventListener("click", () => {
  requestNavigation(() => switchView("chat"));
});

els.chatNotifications.addEventListener("click", (event) => {
  const button = event.target.closest("[data-open-chat-order]");
  if (!button) return;
  openOrderConversation(button.dataset.openChatOrder, button.dataset.chatMessageId);
});

els.chatConversationList.addEventListener("click", (event) => {
  const button = event.target.closest("[data-chat-order]");
  if (!button) return;
  openOrderConversation(button.dataset.chatOrder);
});

els.chatOrderSearchInput.addEventListener("input", (event) => {
  chatOrderSearchQuery = event.target.value;
  renderChatConversationList();
});

els.chatSearchInput.addEventListener("input", (event) => {
  chatSearchQuery = event.target.value;
  renderChat();
});

els.chatFilterSelect.addEventListener("change", (event) => {
  chatFilter = event.target.value;
  renderChat();
});

els.chatMessages.addEventListener("click", (event) => {
  const reply = event.target.closest("[data-reply-message]");
  if (reply) return beginChatReply(reply.dataset.replyMessage);
  const edit = event.target.closest("[data-edit-message]");
  if (edit) return beginChatEdit(edit.dataset.editMessage);
  const remove = event.target.closest("[data-remove-message]");
  if (remove) return removeChatMessage(remove.dataset.removeMessage);
  const jump = event.target.closest("[data-jump-message]");
  if (jump) document.getElementById(`chat-${jump.dataset.jumpMessage}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
});

els.chatReplyPreview.addEventListener("click", (event) => {
  if (event.target.closest("[data-cancel-edit]")) {
    editingChatMessageId = "";
    els.chatMessageInput.value = getSavedChatDraft(getOrder()?.id);
    els.sendChatBtn.textContent = "Send";
  } else if (event.target.closest("[data-cancel-reply]")) {
    chatReplyToId = "";
  } else {
    return;
  }
  renderChatReplyPreview(getOrder());
});

els.chatOrderContext.addEventListener("click", (event) => {
  const button = event.target.closest("[data-open-order]");
  if (!button) return;
  requestNavigation(() => openOrderWorkspace(button.dataset.openOrder, button.dataset.openStage));
});

els.markChatReadBtn.addEventListener("click", () => {
  const order = getOrder();
  if (!order) return;
  markOrderChatRead(order.id);
  render();
});

els.chatTagSelect.addEventListener("change", handleChatTagChange);
els.sendChatBtn.addEventListener("click", sendChatMessage);
els.chatFileInput.addEventListener("change", () => {
  const files = Array.from(els.chatFileInput.files || []);
  els.chatAttachmentSummary.textContent = files.length ? `${files.length} file(s) selected` : "";
});
els.chatMessageInput.addEventListener("input", () => {
  const order = getOrder();
  if (order && !editingChatMessageId) saveChatDraft(order.id, els.chatMessageInput.value);
});
els.chatMessageInput.addEventListener("keydown", (event) => {
  if (event.key !== "Enter" || event.shiftKey) return;
  event.preventDefault();
  sendChatMessage();
});

els.openOrderChatBtn.addEventListener("click", () => {
  orderChatDrawerOpen = true;
  renderOrderChatDrawer();
});
els.cancelOrderBtn.addEventListener("click", () => {
  if (!canCancelOrder()) return;
  requestNavigation(() => openOrderActionDialog("status"));
});
els.deleteOrderBtn.addEventListener("click", () => {
  if (getPermanentDeleteBlocker(getOrder())) return;
  requestNavigation(() => openOrderActionDialog("delete"));
});
els.orderActionConfirmInput.addEventListener("input", () => {
  const order = getOrder();
  els.confirmOrderActionBtn.disabled = pendingOrderAction === "delete" && els.orderActionConfirmInput.value.trim() !== order?.id;
});
els.closeOrderActionBtn.addEventListener("click", closeOrderActionDialog);
els.confirmOrderActionBtn.addEventListener("click", performOrderAction);
els.closeOrderChatBtn.addEventListener("click", () => {
  orderChatDrawerOpen = false;
  renderOrderChatDrawer();
});
els.sendDrawerChatBtn.addEventListener("click", sendDrawerChatMessage);
els.drawerChatInput.addEventListener("keydown", (event) => {
  if (event.key !== "Enter" || event.shiftKey) return;
  event.preventDefault();
  sendDrawerChatMessage();
});

els.overviewTabBtn.addEventListener("click", () => {
  requestNavigation(() => switchView("overview"));
});

els.overviewView.addEventListener("click", (event) => {
  const orderButton = event.target.closest("[data-open-order]");
  if (!orderButton) return;
  requestNavigation(() => openOrderWorkspace(orderButton.dataset.openOrder, orderButton.dataset.openStage));
});

els.specialViewTabBtn.addEventListener("click", () => {
  if (!canViewSpecial()) return;
  requestNavigation(() => switchView("special"));
});

els.specialView.addEventListener("click", (event) => {
  const nav = event.target.closest("[data-special-month-nav]");
  if (nav) {
    specialCalendarCursor = new Date(
      specialCalendarCursor.getFullYear(),
      specialCalendarCursor.getMonth() + Number(nav.dataset.specialMonthNav),
      1,
    );
    render();
    return;
  }

  if (event.target.closest("[data-special-today]")) {
    specialCalendarCursor = getMalaysiaDate();
    render();
    return;
  }

  const orderButton = event.target.closest("[data-open-order]");
  if (!orderButton) return;
  requestNavigation(() => openOrderWorkspace(orderButton.dataset.openOrder));
});

els.specialView.addEventListener("change", (event) => {
  if (event.target.dataset.specialMonth !== undefined) {
    specialCalendarCursor = new Date(specialCalendarCursor.getFullYear(), Number(event.target.value), 1);
    render();
  }

  if (event.target.dataset.specialYear !== undefined) {
    specialCalendarCursor = new Date(Number(event.target.value), specialCalendarCursor.getMonth(), 1);
    render();
  }
});

els.workflowTabBtn.addEventListener("click", () => {
  requestNavigation(() => switchView("workflow"));
});

els.workflowBypassNotice.addEventListener("click", (event) => {
  if (event.target.closest("#bypassToProcurementBtn")) {
    bypassToProcurement();
    return;
  }
  const stageButton = event.target.closest("[data-bypass-stage]");
  if (!stageButton || stageButton.disabled) return;
  const stageIndex = Number(stageButton.dataset.bypassStage);
  const order = getOrder();
  if (!order || stageIndex > order.stageIndex || !canViewStage(STAGES[stageIndex])) return;
  requestNavigation(() => {
    selectedStageIndex = stageIndex;
    formDraftKey = "";
    render();
  });
});

els.adminContent.addEventListener("change", handleAdminChange);

els.resetAccessBtn.addEventListener("click", () => {
  if (!canManageAccess()) return;
  state.access = structuredClone(DEFAULT_ACCESS);
  render();
});

els.stayOnPageBtn.addEventListener("click", closeNavigationGuard);
els.discardAndContinueBtn.addEventListener("click", () => completePendingNavigation("discard"));
els.saveAndContinueBtn.addEventListener("click", () => completePendingNavigation("save"));

setInterval(() => {
  renderSystemClock();
  if (activeView !== "workflow") return;
  const order = getOrder();
  if (!order) return;
  const stage = getCurrentStage(order);
  if (!stage || (!canViewAll() && !canViewStage(stage))) return;
  if (selectedStageIndex < order.stageIndex) return;
  renderWorkflowCountdown(order, stage, getGate(stage, order));
}, 1000);

els.authForm.addEventListener("submit", handleAuthSubmit);
els.forgotPasswordBtn.addEventListener("click", handleForgotPassword);
els.logoutBtn.addEventListener("click", handleLogout);

renderAuthentication();
initialiseIdentity();
